import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, BarChart, Bar
} from 'recharts';
import { 
  ChevronDown, DollarSign, BookOpen, CreditCard, Gift,
  Layout, History, FileText, PieChart, Zap, TrendingDown,
  TrendingUp, ShieldAlert, Activity, Calendar, Users, X,
  Table, Trophy, Award, AlertTriangle
} from 'lucide-react';
import {
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaChevronLeft,
  FaChevronRight,
  FaInfo,
  FaFileCsv
} from "react-icons/fa";
import Papa from "papaparse";
import Select from "react-select";
import './Style.css';
import { BackOfficeHeader } from "./GameSessionHeader";
import { formatTransactionCode, DEPOSIT_CODES, WITHDRAWAL_CODES, BOOK_CODES, RAKE_CODES, TIP_CODES,
  WALLET_INCREASE_CODES, WALLET_DECREASE_CODES } 
from "./utils/transactionUtils";

export default function Dashboard() {
  const { cardroom } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [timeframe, setTimeframe] = useState("week");
  const [customDates, setCustomDates] = useState({ start: "", end: "" });
  const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);
  const [sortConfig, setSortConfig] = useState({ key: 'total_profit', direction: 'desc' });
  const [visibleSeries, setVisibleSeries] = useState({
    total_profit: true,
    rake: false,
    deposits_total: false,
    withdrawals_total: false,
    debt_gained: false,
    balance_gained: false,
    costs: true,
    dealers_tips: false,
    service_staff_tips: false,
    final_book: true,
    max_total_credit: false,
    bonuses_total: false
  });
  const [performanceView, setPerformanceView] = useState('graph');
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [showDealers, setShowDealers] = useState(false);
  const [showBookChanges, setShowBookChanges] = useState(false);
  const [openDealers, setOpenDealers] = useState({});
  const [dealerRecords, setDealerRecords] = useState([]);
  const navigate = useNavigate();

  const [openSessions, setOpenSessions] = useState({});
  const [activeSessionView, setActiveSessionView] = useState({});
  const [sessionTables, setSessionTables] = useState({});
  const [sessionTournaments, setSessionTournaments] = useState({});
  const [sessionTransactions, setSessionTransactions] = useState({});
  
  const [selectedSession, setSelectedSession] = useState(null);
  const [modalTab, setModalTab] = useState('tables');

  // Modal specific states from Data.jsx
  const [showBookReportModal, setShowBookReportModal] = useState(false);
  const [bookReportSessionId, setBookReportSessionId] = useState(null);
  const [bookReportPlayerId, setBookReportPlayerId] = useState("");
  const [bookReportTransactions, setBookReportTransactions] = useState(null);
  const [bookReportChanges, setBookReportChanges] = useState([]);

  const [showWalletReportModal, setShowWalletReportModal] = useState(false);
  const [walletReportSessionId, setWalletReportSessionId] = useState(null);
  const [walletReportWalletId, setWalletReportWalletId] = useState("");
  const [walletReportTransactions, setWalletReportTransactions] = useState(null);
  const [walletReportChanges, setWalletReportChanges] = useState([]);
  const [allWallets, setAllWallets] = useState([]);

  const [selectedSessionCashId, setSelectedSessionCashId] = useState(null);
  const [sessionCashTransactions, setSessionCashTransactions] = useState({});

  const token = localStorage.getItem("token");

  const [showBonusModal, setShowBonusModal] = useState(false);
  const [bonusSessionId, setBonusSessionId] = useState(null);
  const [bonusTransactions, setBonusTransactions] = useState([]);

  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [sessionPayoutFilters, setSessionPayoutFilters] = useState({
    staff: true,
    costs: true,
    wallets: true,
  });

  const [selectedTableId, setSelectedTableId] = useState(null);
  const [selectedTableTransactions, setSelectedTableTransactions] = useState([]);
  const [txFilters, setTxFilters] = useState({
    rake: true,
    tips: true,
    deposit: true,
    withdrawal: true,
    book: true,
  });
  const [historySearchType, setHistorySearchType] = useState("player");
  const [historySearchEntity, setHistorySearchEntity] = useState(null);
  const [historyResults, setHistoryResults] = useState([]);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyTxFilters, setHistoryTxFilters] = useState({
    deposit: true,
    withdrawal: true,
    book: true,
  });
  const [hoveredHistoryTarget, setHoveredHistoryTarget] = useState(null);


  useEffect(() => {
    if (!token) {
      navigate(`/`);
      return;
    }
    if (dealerMode === 2) {
      navigate(`/${cardroom}/dealerdashboard`);
      return;
    } else if (!permission?.includes("V")){
      navigate('/');
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }, [token, cardroom, navigate, dealerMode, permission]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = { timeframe };
      if (timeframe === "custom") {
        params.startDate = customDates.start;
        params.endDate = customDates.end;
      }
      const { data } = await axios.get(`/${cardroom}/api/dashboard/stats`, { params });
      setData(data);
    } catch (err) {
      console.error("Failed to fetch dashboard stats", err);
    } finally {
      setLoading(false);
    }
  }, [cardroom, timeframe, customDates, navigate]);

  const fetchDealerRecords = useCallback(async () => {
    try {
      const params = { timeframe };
      if (timeframe === "custom") {
        params.startDate = customDates.start;
        params.endDate = customDates.end;
      }
      const { data } = await axios.get(`/${cardroom}/api/dealer_records`, { params });
      setDealerRecords(data);
    } catch (err) {
      console.error("Failed to fetch dealer records", err);
    }
  }, [cardroom, timeframe, customDates]);

  const toggleSession = (sessionId) => {
    setOpenSessions(prev => ({
      ...prev,
      [sessionId]: !prev[sessionId]
    }));
  };


  const showTables = async (sessionId) => {
    setActiveSessionView(prev => ({
      ...prev,
      [sessionId]: prev[sessionId] === 'tables' ? null : 'tables'
    }));
    if (!sessionTables[sessionId]) {
      try {
        const { data } = await axios.get(`/${cardroom}/api/tables/session/${sessionId}`);
        setSessionTables(prev => ({ ...prev, [sessionId]: data }));
      } catch (err) {
        console.error("Failed to load session tables", err);
      }
    }
  };

  const showTournaments = async (sessionId) => {
    setActiveSessionView(prev => ({
      ...prev,
      [sessionId]: prev[sessionId] === 'tournaments' ? null : 'tournaments'
    }));
    if (!sessionTournaments[sessionId]) {
      
      try {
        const { data } = await axios.get(`/${cardroom}/api/sessions/${sessionId}/tournaments`);
        setSessionTournaments(prev => ({ ...prev, [sessionId]: data }));
      } catch (err) {
        console.error("Failed to load session tournaments", err);
      }
    }
  };

  useEffect(() => {
    const loadWallets = async () => {
      
      try {
        const { data } = await axios.get(`/${cardroom}/api/wallets`);
        setAllWallets(data);
      } catch (err) {
        console.error("Failed to load wallets", err);
      }
    };
    loadWallets();
  }, [cardroom]);

  const loadBookChanges = async (sessionId) => {
    
    try {
      const { data } = await axios.get(`/${cardroom}/api/sessions/${sessionId}/book_changes`);
      setBookReportChanges(data);
    } catch (err) {
      console.error("Load book changes failed", err);
      setBookReportChanges([]);
    }
  };

  const loadWalletChanges = async (sessionId) => {
    
    try {
      const { data } = await axios.get(`/${cardroom}/api/sessions/${sessionId}/wallet_changes`);
      setWalletReportChanges(data);
    } catch (err) {
      console.error("Load wallet changes failed", err);
      setWalletReportChanges([]);
    }
  };

  const loadSessionTransactions = async (sessionId) => {
    
    try {
      const { data } = await axios.get(`/${cardroom}/api/sessions/${sessionId}/transactions`);
      setSessionTransactions(prev => ({ ...prev, [sessionId]: data }));
    } catch (err) {
      console.error("Error loading session transactions:", err);
    }
  };

  const listSessionCashTransactions = async (sessionId) => {
    setSelectedSessionCashId(sessionId);
    if (sessionCashTransactions[sessionId]) return;
    
    try {
      const { data } = await axios.get(`/${cardroom}/api/sessions/${sessionId}/cashbag_transactions`);
      setSessionCashTransactions(prev => ({ ...prev, [sessionId]: data }));
    } catch (err) {
      console.error("List session cash transactions failed", err);
    }
  };

  const loadBonusTransactions = async (sessionId) => {
    
    try {
      const { data } = await axios.get(`/${cardroom}/api/sessions/${sessionId}/bonuses`);
      setBonusTransactions(data);
    } catch (err) {
      console.error("Load bonus transactions failed", err);
      setBonusTransactions([]);
    }
  };

  const exportToCSV = (title, headers, data, e) => {
    e.stopPropagation();
    if (!data || data.length === 0) return;
    const csv = Papa.unparse({ fields: headers, data });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${title}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDateFull = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatSessionDayName = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'long' });
  };

  const formatChartDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const searchBookReport = async (playerId = bookReportPlayerId) => {
    if (!playerId) return;
    
    try {
      const { data } = await axios.get(`/${cardroom}/api/sessions/${bookReportSessionId}/players/${playerId}/book_transactions`);
      setBookReportTransactions(data);
    } catch (err) {
      console.error("Search book report failed", err);
      setBookReportTransactions([]);
    }
  };

  const searchWalletReport = async (walletId = walletReportWalletId) => {
    if (!walletId) return;
    
    try {
      const { data } = await axios.get(`/${cardroom}/api/sessions/${walletReportSessionId}/wallets/${walletId}/transactions`);
      setWalletReportTransactions(data);
    } catch (err) {
      console.error("Search wallet report failed", err);
      setWalletReportTransactions([]);
    }
  };

  const formatHistoryDateTime = (date, endOfDay = false) => {
    if (!date) return "";

    const nextDate = date instanceof Date ? new Date(date) : new Date(date);
    if (Number.isNaN(nextDate.getTime())) return "";

    if (endOfDay) {
      nextDate.setHours(23, 59, 59, 999);
    }

    const pad = (value) => String(value).padStart(2, "0");
    return [
      nextDate.getFullYear(),
      pad(nextDate.getMonth() + 1),
      pad(nextDate.getDate()),
    ].join("-") + " " + [
      pad(nextDate.getHours()),
      pad(nextDate.getMinutes()),
      pad(nextDate.getSeconds()),
    ].join(":");
  };

  const getDashboardHistoryRange = () => {
    const now = new Date();

    if (timeframe === "custom") {
      return {
        startTime: customDates.start ? formatHistoryDateTime(`${customDates.start}T00:00:00`) : "",
        endTime: customDates.end ? formatHistoryDateTime(`${customDates.end}T00:00:00`, true) : "",
      };
    }

    if (timeframe === "week") {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      return {
        startTime: formatHistoryDateTime(start),
        endTime: formatHistoryDateTime(now),
      };
    }

    if (timeframe === "month") {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      return {
        startTime: formatHistoryDateTime(start),
        endTime: formatHistoryDateTime(now),
      };
    }

    if (timeframe === "year") {
      const start = new Date(now);
      start.setDate(start.getDate() - 365);
      return {
        startTime: formatHistoryDateTime(start),
        endTime: formatHistoryDateTime(now),
      };
    }

    if (timeframe === "last_session" && sessions.length) {
      const session = sessions[0];
      return {
        startTime: formatHistoryDateTime(session.start_time),
        endTime: formatHistoryDateTime(session.end_time || session.start_time),
      };
    }

    return { startTime: "", endTime: "" };
  };

  const openDashboardHistory = async (type, entity, reset = true) => {
    if (!entity?.value) return;

    setIsHistoryLoading(true);
    const newOffset = reset ? 0 : historyOffset;

    try {
      const endpoint =
        type === "wallet"
          ? `/${cardroom}/api/history/wallets/${entity.value}/transactions`
          : `/${cardroom}/api/history/players/${entity.value}/transactions`;
      const range = getDashboardHistoryRange();
      const params = {
        limit: 25,
        offset: newOffset,
      };

      if (range.startTime) params.startTime = range.startTime;
      if (range.endTime) params.endTime = range.endTime;

      const { data } = await axios.get(endpoint, { params });

      if (reset) {
        setHistorySearchType(type);
        setHistorySearchEntity(entity);
        setHistoryResults(data);
        setShowHistoryModal(true);
      } else {
        setHistoryResults((prev) => [...prev, ...data]);
      }

      setHistoryOffset(newOffset + 25);
      setHasMoreHistory(data.length === 25);
    } catch (err) {
      console.error("History search failed", err);
      setHistoryResults([]);
      setShowHistoryModal(true);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const loadMoreDashboardHistory = () => {
    openDashboardHistory(historySearchType, historySearchEntity, false);
  };

  const closeDashboardHistory = () => {
    setShowHistoryModal(false);
    setHistoryResults([]);
    setHistoryOffset(0);
    setHasMoreHistory(false);
  };

  const getFilteredHistoryResults = () => {
    return historyResults.filter((tx) => {
      if (historySearchType !== "player") return true;

      const code = Number(tx.code);
      const isDeposit = DEPOSIT_CODES.includes(code) || tx.type === "deposit";
      const isWithdrawal = WITHDRAWAL_CODES.includes(code) || tx.type === "withdrawal";
      const isBook = BOOK_CODES.includes(code);

      if (isDeposit && historyTxFilters.deposit) return true;
      if (isWithdrawal && historyTxFilters.withdrawal) return true;
      if (isBook && historyTxFilters.book) return true;
      if (!isDeposit && !isWithdrawal && !isBook) return true;
      return false;
    });
  };

  const getFilteredTableTransactions = () => {
    return selectedTableTransactions.filter((t) => {
      const tk = String(t.type || "").toLowerCase();
      const code = t.code;

      const isRake = (code && RAKE_CODES.includes(code)) || tk.includes("rake");
      const isTip = (code && TIP_CODES.includes(code)) || tk === "tip";
      const isDeposit = (code && DEPOSIT_CODES.includes(code)) || tk === "deposit";
      const isWithdrawal = (code && WITHDRAWAL_CODES.includes(code)) || tk === "withdrawal";
      const isBook = (code && BOOK_CODES.includes(code)) || tk === "debt payback" || tk === "session credit payback";

      let isMatched = false;
      let show = false;

      if (isRake) {
        isMatched = true;
        if (txFilters.rake) show = true;
      }
      if (isTip) {
        isMatched = true;
        if (txFilters.tips) show = true;
      }
      if (isDeposit) {
        isMatched = true;
        if (txFilters.deposit) show = true;
      }
      if (isWithdrawal) {
        isMatched = true;
        if (txFilters.withdrawal) show = true;
      }
      if (isBook) {
        isMatched = true;
        if (txFilters.book) show = true;
      }

      if (isMatched) return show;
      return true;
    });
  };

  const exportDashboardReport = (e) => {
    e.stopPropagation();
    const grid = [];
    const dateStr = new Date().toISOString().split('T')[0];

    const setCell = (r, c, val) => {
      if (!grid[r]) grid[r] = [];
      grid[r][c] = val;
    };

    // --- SECTION 1: SUMMARY STATISTICS (Cols 0-2) ---
    setCell(0, 0, "--- SUMMARY STATISTICS ---");
    const summaryRows = [
      ["Metric", "Value", "Subtitle Detail"],
      ["Net Profit", `$${summary?.totalCashGameProfit?.toLocaleString()}`, `$${summary?.totalTournamentProfit?.toLocaleString()} tournament profit`],
      ["Total Rake", `$${summary?.totalRake?.toLocaleString()}`, `$${(summary?.avgEfficiency || 0).toFixed(0)} rake per hour`],
      ["Total Costs", `$${summary?.totalCosts?.toLocaleString()}`, `${((summary?.totalCosts / (summary?.totalRake || 1)) * 100).toFixed(1)}% of rake`],
      ["Total Deposits", `$${summary?.totalDeposits?.toLocaleString()}`, `$${summary?.totalBonuses?.toLocaleString()} total bonuses`],
      ["Debt Gained", `$${summary?.totalDebtGained?.toLocaleString()}`, `${((summary?.totalDebtGained / (summary?.totalDeposits || 1)) * 100).toFixed(1)}% of deposits`],
      ["Dealer Tips", `$${summary?.totalDealersTips?.toLocaleString()}`, `${((summary?.totalDealersTips / (summary?.totalDeposits || 1)) * 100).toFixed(1)}% of deposits`],
      ["Total Withdrawals", `$${summary?.totalWithdrawals?.toLocaleString()}`, ""],
      ["Balances Increase", `$${summary?.totalBalanceGained?.toLocaleString()}`, `${((summary?.totalBalanceGained / (summary?.totalWithdrawals || 1)) * 100).toFixed(1)}% of withdrawals`],
      ["Staff Tips", `$${summary?.totalServiceStaffTips?.toLocaleString()}`, ""],
      ["Operational Flow", `${summary?.totalTablesCount} tables`, summary?.totalPlayingTime || "0:00"]
    ];
    summaryRows.forEach((row, i) => {
      row.forEach((cell, j) => setCell(i + 1, j, cell));
    });

    let currentColumn = 4; // Start Trends after a spacer column

    // --- SECTION 2: PERFORMANCE TRENDS (Cols 4+) ---
    if (sessions.length > 0) {
      setCell(0, currentColumn, "--- PERFORMANCE TRENDS ---");
      const headers = ["Time", "Total Profit"];
      const keys = ["total_profit"];
      Object.keys(visibleSeries).forEach(key => {
        if (visibleSeries[key] && key !== "total_profit") {
          const label = key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
          headers.push(label);
          keys.push(key);
        }
      });

      headers.forEach((h, j) => setCell(1, currentColumn + j, h));
      
      sessions.forEach((s, i) => {
        setCell(i + 2, currentColumn, new Date(s.start_time).toLocaleString());
        setCell(i + 2, currentColumn + 1, s.total_profit);
        
        for (let j = 2; j < headers.length; j++) {
          setCell(i + 2, currentColumn + j, s[keys[j-1]] || 0);
        }
      });
      currentColumn += headers.length + 1;
    }

    // --- SECTION 3: BOOK CHANGES ---
    if (bookChanges?.length > 0) {
      setCell(0, currentColumn, "--- BOOK CHANGES ---");
      const bookHeaders = ["Player Name", "Old Book", "New Book", "Net Change"];
      bookHeaders.forEach((h, j) => setCell(1, currentColumn + j, h));
      
      bookChanges.filter(b => b.net_change !== 0).forEach((b, i) => {
        const row = [b.name, b.old_book, b.new_book, b.net_change];
        row.forEach((cell, j) => setCell(i + 2, currentColumn + j, cell));
      });
      currentColumn += bookHeaders.length + 1;
    }

    // --- SECTION 4: WALLET CHANGES ---
    if (walletChanges?.length > 0) {
      setCell(0, currentColumn, "--- WALLET CHANGES ---");
      const walletHeaders = ["Wallet Name", "Old Balance", "New Balance", "Difference"];
      walletHeaders.forEach((h, j) => setCell(1, currentColumn + j, h));
      
      walletChanges.filter(w => w.old_balance !== w.new_balance).forEach((w, i) => {
        const row = [w.name, w.old_balance, w.new_balance, w.new_balance - w.old_balance];
        row.forEach((cell, j) => setCell(i + 2, currentColumn + j, cell));
      });
    }

    const csvContent = grid.map(row => {
      const line = [];
      const maxCol = row ? row.length : 0;
      for (let c = 0; c < maxCol; c++) {
        const cell = row[c] === undefined || row[c] === null ? "" : row[c];
        const val = String(cell).replace(/"/g, '""');
        line.push(`"${val}"`);
      }
      return line.join(",");
    }).join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Dashboard_Report_${dateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const loadTableTransactions = async (tableId) => {
    
    try {
      const { data } = await axios.get(`/${cardroom}/api/tables/${tableId}/transactions`);
      setSelectedTableTransactions(data);
      setSelectedTableId(tableId);
    } catch (err) {
      console.error("Error loading table transactions:", err);
    }
  };



  useEffect(() => {
    fetchStats();
    fetchDealerRecords();
  }, [fetchStats, fetchDealerRecords]);

  useEffect(() => {
    if (selectedSession) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [selectedSession]);

  const sortedPlayers = React.useMemo(() => {
    if (!data?.players) return [];
    let sortableItems = [...data.players];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        // Alphabetical sort for name
        if (sortConfig.key === 'name') {
          const aStr = String(aValue || '');
          const bStr = String(bValue || '');
          return sortConfig.direction === 'asc' 
            ? aStr.localeCompare(bStr)
            : bStr.localeCompare(aStr);
        }

        // Numerical sort for all other columns
        const aNum = Number(aValue) || 0;
        const bNum = Number(bValue) || 0;

        if (aNum < bNum) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aNum > bNum) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [data?.players, sortConfig]);

  const requestSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const selectCalendarMonth = (monthDate) => {
    const nextMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    setCalendarMonth(nextMonth);
  };


  const { summary = {}, sessions = [], players = [], walletChanges = [], bookChanges = [], historicalAvg = {} } = data || {};
  const performanceTrendData = [...sessions].reverse().map((session) => {
    const dayName = formatSessionDayName(session.start_time);
    const dateLabel = formatChartDate(session.start_time);

    return {
      ...session,
      session_axis_label: `${dayName}|${dateLabel}`,
      session_tooltip_label: `${dayName}, ${formatDateFull(session.start_time)}`
    };
  });
  const performanceSeries = [
    { key: 'total_profit', name: 'Profit', color: '#10b981', areaFill: 'url(#colorProfit)', strokeWidth: 3 },
    { key: 'rake', name: 'Rake', color: '#8b5cf6' },
    { key: 'deposits_total', name: 'Deposits', color: '#efeff7ff' },
    { key: 'withdrawals_total', name: 'Withdraws', color: '#fb923c' },
    { key: 'debt_gained', name: 'Debt', color: '#eab308' },
    { key: 'balance_gained', name: 'Balance', color: '#ec4899' },
    { key: 'costs', name: 'Costs', color: '#f43f5e' },
    { key: 'dealers_tips', name: 'D.Tips', color: '#06b6d4' },
    { key: 'service_staff_tips', name: 'S.Tips', color: '#14b8a6' },
    { key: 'final_book', name: 'Book', color: '#3b82f6' },
    { key: 'max_total_credit', name: 'Max Credit', color: '#e1d11fff' },
    { key: 'bonuses_total', name: 'Bonuses', color: '#e038d8ff' },
  ];
  const usePerformanceBarChart = timeframe === "last_session" || sessions.length === 1;
  const calendarSessionsByDay = sessions.reduce((acc, session) => {
    const dateKey = getLocalDateKey(session.start_time);
    if (!dateKey) return acc;

    if (!acc[dateKey]) {
      acc[dateKey] = {
        dateKey,
        sessions: 0,
        totalProfit: 0,
        rake: 0,
        costs: 0,
        tips: 0,
        bookChange: 0,
        deposits: 0,
        withdrawals: 0,
        tables: 0,
        players: 0,
      };
    }

    acc[dateKey].sessions += 1;
    acc[dateKey].totalProfit += Number(session.total_profit) || 0;
    acc[dateKey].rake += Number(session.rake) || 0;
    acc[dateKey].costs += Number(session.costs) || 0;
    acc[dateKey].tips += (Number(session.dealers_tips) || 0) + (Number(session.service_staff_tips) || 0);
    acc[dateKey].bookChange += (Number(session.debt_gained) || 0) - (Number(session.balance_gained) || 0);
    acc[dateKey].deposits += Number(session.deposits_total) || 0;
    acc[dateKey].withdrawals += Number(session.withdrawals_total) || 0;
    acc[dateKey].tables += Number(session.tables_count) || 0;
    acc[dateKey].players += Number(session.players_count) || 0;

    return acc;
  }, {});
  const calendarDays = buildCalendarDays(calendarMonth, calendarSessionsByDay);

  return (
    <div className="game">
      <BackOfficeHeader
        activePage="dashboard"
        cardroom={cardroom}
        centerTitle="Dashboard"
        dealerMode={dealerMode}
        title="Analytics"
      />
<div className="game-container">
        {loading && !data ? (
          <div className="loading-container" style={{ padding: "4rem", textAlign: "center", color: "white" }}>
            <h2 style={{ marginBottom: "1.5rem", fontWeight: "700" }}>Gathering Data...</h2>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div className="spinner" style={{ border: "4px solid rgba(255,255,255,0.1)", borderTop: "4px solid #6366f1", borderRadius: "50%", width: "50px", height: "50px", animation: "spin 1s linear infinite" }}></div>
            </div>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
        ) : !data ? (
          <div className="error-container" style={{ padding: "4rem", textAlign: "center", color: "white" }}>
            <h2 style={{ color: "#f43f5e", marginBottom: "1rem" }}>Connection Lost</h2>
            <p style={{ color: "#94a3b8", marginBottom: "2rem" }}>Failed to retrieve dashboard analytics.</p>
            <button onClick={fetchStats} className="game-btn game-btn--primary">Retry Sync</button>
          </div>
        ) : (
          <>
            <div className="dashboard-controls" style={{ marginBottom: "2rem" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
                  <div className="timeframe-selector" style={{ display: "flex", gap: "0.5rem", background: "#1e293b", padding: "0.4rem", borderRadius: "0.75rem" }}>
                    {["last_session", "week", "month", "year", "custom"].map((tf) => (
                      <button
                        key={tf}
                        onClick={() => setTimeframe(tf)}
                        style={{
                          padding: "0.5rem 1rem",
                          borderRadius: "0.5rem",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          fontWeight: "600",
                          transition: "all 0.2s",
                          backgroundColor: timeframe === tf ? "#6366f1" : "transparent",
                          color: timeframe === tf ? "white" : "#94a3b8"
                        }}
                      >
                        {tf.replace("_", " ").toUpperCase()}
                      </button>
                    ))}
                  </div>

                  {timeframe === "custom" && (
                    <div className="custom-dates" style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                      <input 
                        type="date" 
                        value={customDates.start} 
                        onChange={(e) => setCustomDates(p => ({...p, start: e.target.value}))}
                        style={{ padding: "0.5rem", borderRadius: "0.5rem", border: "1px solid #334155", background: "#1e293b", color: "white", fontSize: "0.875rem" }}
                      />
                      <span style={{ color: "#94a3b8", fontSize: "0.875rem" }}>to</span>
                      <input 
                        type="date" 
                        value={customDates.end} 
                        onChange={(e) => setCustomDates(p => ({...p, end: e.target.value}))}
                        style={{ padding: "0.5rem", borderRadius: "0.5rem", border: "1px solid #334155", background: "#1e293b", color: "white", fontSize: "0.875rem" }}
                      />
                      <button onClick={fetchStats} className="apply-btn" style={{ padding: "0.5rem 1rem", background: "#6366f1", borderRadius: "0.5rem", border: "none", color: "white", cursor: "pointer", fontWeight: "600", fontSize: "0.875rem" }}>
                        Apply
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ marginLeft: "auto" }}>
                  <button 
                    onClick={exportDashboardReport}
                    className="game-btn game-btn--success"
                  >
                    <FaFileCsv size={18} />
                    <span>Export Dashboard</span>
                  </button>
                </div>
              </div>
            </div>

      {/* Summary Cards */}
      {sessions.length === 0 ? (
        <div className="no-data-message" style={{ padding: "4rem", textAlign: "center", background: "#1e293b", borderRadius: "1rem", border: "1px solid #334155", margin: "2rem 0" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "1.5rem" }}>
            <div style={{ background: "#0f172a", padding: "1.5rem", borderRadius: "50%", border: "1px solid #334155" }}>
              <Calendar size={48} color="#94a3b8" style={{ opacity: 0.5 }} />
            </div>
          </div>
          <h3 style={{ color: "white", marginBottom: "0.5rem", fontSize: "1.5rem", fontWeight: "700" }}>No Sessions Found</h3>
          <p style={{ color: "#94a3b8", maxWidth: "400px", margin: "0 auto", lineHeight: "1.6" }}>
            There are no recorded sessions for the selected timeframe: <strong style={{color: "#6366f1"}}>{timeframe.replace("_", " ").toUpperCase()}</strong>. Try selecting a different period.
          </p>
        </div>
      ) : (
        <>
          {/* Summary Cards Row 1 */}
          <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1.5rem", marginBottom: "1rem" }}>
        <SummaryCard 
          title="Net Profit" 
          value={`$${summary?.totalCashGameProfit?.toLocaleString()}`} 
          subValue={`$${summary?.totalTournamentProfit?.toLocaleString()} tournament profit`}
          icon={<DollarSign size={24} color="#3182ce" />}
          description="Profit from cash games"
        />
        <SummaryCard 
          title="Total Rake" 
          value={`$${summary?.totalRake?.toLocaleString()}`} 
          subValue={`$${(summary?.avgEfficiency || 0).toFixed(0)} rake per hour`}
          icon={<Zap size={24} color="#3182ce" />}
          description="Sum of all tables' rake"
        />
        <SummaryCard 
          title="Total Costs" 
          value={`$${summary?.totalCosts?.toLocaleString()}`} 
          subValue={`${((summary?.totalCosts / (summary?.totalRake || 1)) * 100).toFixed(1)}% of rake`}
          icon={<TrendingDown size={24} color="#f43f5e" />}
          description="Session expenses relative to profit"
        />
        <SummaryCard 
          title="Total Deposits" 
          value={`$${summary?.totalDeposits?.toLocaleString()}`} 
          subValue={`$${summary?.totalBonuses?.toLocaleString()} total bonuses`}
          icon={<CreditCard size={24} color="#10b981" />}
          description="Sum of all player deposits"
        />
        <SummaryCard 
          title="Debt Gained" 
          value={`$${summary?.totalDebtGained?.toLocaleString()}`} 
          subValue={`${((summary?.totalDebtGained / (summary?.totalDeposits || 1)) * 100).toFixed(1)}% of deposits`}
          icon={<ShieldAlert size={24} color="#fbbf24" />}
          description="Player debt changes relative to deposits"
        />
      </div>

      {/* Advanced Toggle (Show) */}
      {!showAdvanced && (
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "1.5rem" }}>
          <button 
            onClick={() => setShowAdvanced(true)}
            style={{ 
              background: "#1e293b", 
              border: "1px solid #334155", 
              color: "#94a3b8", 
              padding: "0.5rem 1.5rem", 
              borderRadius: "0.5rem", 
              cursor: "pointer",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.2s"
            }}
          >
            <FaChevronDown />
            Show Advanced Metrics
          </button>
        </div>
      )}

      {/* Summary Cards Row 2 (Advanced) */}
      {showAdvanced && (
        <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1.5rem", marginBottom: "2rem" }}>
          <SummaryCard 
            title="Dealer Tips" 
            value={`$${summary?.totalDealersTips?.toLocaleString()}`} 
            subValue={`${((summary?.totalDealersTips / (summary?.totalDeposits || 1)) * 100).toFixed(1)}% of deposits`}
            icon={<Award size={24} color="#10b981" />}
            description="Sum of all tips relative to deposits"
          />
          <SummaryCard 
            title="Total Withdrawals" 
            value={`$${summary?.totalWithdrawals?.toLocaleString()}`} 
            icon={<CreditCard size={24} color="#f43f5e" />}
            description="Sum of all player withdrawals"
          />
          <SummaryCard 
            title="Balances Increase" 
            value={`$${summary?.totalBalanceGained?.toLocaleString()}`} 
            subValue={`${((summary?.totalBalanceGained / (summary?.totalWithdrawals || 1)) * 100).toFixed(1)}% of withdrawals`}
            icon={<TrendingUp size={24} color="#10b981" />}
            description="Players' balance increase"
          />
          <SummaryCard 
            title="Staff Tips" 
            value={`$${summary?.totalServiceStaffTips?.toLocaleString()}`} 
            icon={<Users size={24} color="#06b6d4" />}
            description="Sum of all service staff tips"
          />
          <SummaryCard 
            title="Operational Flow" 
            value={`${summary?.totalTablesCount} tables`}
            subValue={summary?.totalPlayingTime || "0:00"}
            icon={<Activity size={24} color="#3182ce" />}
            description={
              <div style={{ fontSize: "0.7rem", color: "#94a3b8", marginTop: "0.25rem" }}>
                Avg: {summary?.avgTablesPerSession?.toFixed(1)} tables/sess | {(summary?.avgPlayingTimeSecPerSession / 3600).toFixed(1)}h/sess
              </div>
            }
          />
        </div>
      )}

      {/* Advanced Toggle (Hide) */}
      {showAdvanced && (
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "2rem" }}>
          <button 
            onClick={() => setShowAdvanced(false)}
            style={{ 
              background: "#1e293b", 
              border: "1px solid #334155", 
              color: "#94a3b8", 
              padding: "0.5rem 1.5rem", 
              borderRadius: "0.5rem", 
              cursor: "pointer",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.2s"
            }}
          >
            <FaChevronUp />
            Hide Advanced Metrics
          </button>
        </div>
      )}

      <div className="main-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: "1.5rem", marginBottom: "2rem" }}>
        
        {/* Row 1: Graph & Calendar */}
        <div style={{ display: "contents" }}>
          
          {/* Performance Trends (Graph) */}
          {sessions.length > 0 && (
            <div style={{ background: "#1e293b", padding: "1.5rem", borderRadius: "1rem", border: "1px solid #334155", gridColumn: "1 / -1" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <Calendar size={20} color="#3182ce" /> Performance Trends ({sessions.length} sessions)
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", alignItems: "flex-end" }}>
                  <div style={{ display: "flex", gap: "0.25rem", background: "#0f172a", border: "1px solid #334155", borderRadius: "0.5rem", padding: "0.25rem" }}>
                    {[
                      { key: 'graph', label: 'Graph' },
                      { key: 'calendar', label: 'Calendar' }
                    ].map(view => (
                      <button
                        key={view.key}
                        onClick={() => setPerformanceView(view.key)}
                        style={{
                          padding: "0.35rem 0.75rem",
                          borderRadius: "0.35rem",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "0.75rem",
                          fontWeight: "700",
                          background: performanceView === view.key ? "#6366f1" : "transparent",
                          color: performanceView === view.key ? "#fff" : "#94a3b8"
                        }}
                      >
                        {view.label}
                      </button>
                    ))}
                  </div>
                  {performanceView === 'graph' ? (
                    <>
  {/* Row 1 */}
  <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", flexWrap: "wrap" }}>
    {[
      { key: 'total_profit', label: 'Profit', color: '#10b981' },
      { key: 'rake', label: 'Rake', color: '#8b5cf6' },
      { key: 'costs', label: 'Costs', color: '#f43f5e' },
      { key: 'bonuses_total', label: 'Bonuses', color: '#e038d8ff' }
    ].map(s => (
      <label key={s.key} style={{ 
        display: "flex", 
        alignItems: "center", 
        gap: "0.3rem", 
        cursor: "pointer", 
        fontSize: "0.7rem", 
        background: visibleSeries[s.key] ? s.color + '20' : "#0f172a", 
        padding: "0.2rem 0.6rem", 
        borderRadius: "9999px", 
        border: `1px solid ${visibleSeries[s.key] ? s.color : '#334155'}`,
        transition: "all 0.2s"
      }}>
        <input 
          type="checkbox" 
          checked={visibleSeries[s.key]} 
          onChange={() => setVisibleSeries(prev => ({ ...prev, [s.key]: !prev[s.key] }))}
          style={{ display: "none" }}
        />
        <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: visibleSeries[s.key] ? s.color : '#475569' }} />
        <span style={{ color: visibleSeries[s.key] ? '#f8fafc' : '#94a3b8', fontWeight: visibleSeries[s.key] ? "700" : "500" }}>
          {s.label}
        </span>
      </label>
    ))}
  </div>
{/* Row 3 */}
  <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", flexWrap: "wrap" }}>
    {[
      { key: 'debt_gained', label: 'Debt', color: '#eab308' },
      { key: 'balance_gained', label: 'Balances', color: '#ec4899' },
      { key: 'max_total_credit', label: 'Max Credit', color: '#e1d11fff' },
      { key: 'final_book', label: 'Book', color: '#3b82f6' }
    ].map(s => (
      <label key={s.key} style={{ 
        display: "flex", 
        alignItems: "center", 
        gap: "0.3rem", 
        cursor: "pointer", 
        fontSize: "0.7rem", 
        background: visibleSeries[s.key] ? s.color + '20' : "#0f172a", 
        padding: "0.2rem 0.6rem", 
        borderRadius: "9999px", 
        border: `1px solid ${visibleSeries[s.key] ? s.color : '#334155'}`,
        transition: "all 0.2s"
      }}>
        <input 
          type="checkbox" 
          checked={visibleSeries[s.key]} 
          onChange={() => setVisibleSeries(prev => ({ ...prev, [s.key]: !prev[s.key] }))}
          style={{ display: "none" }}
        />
        <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: visibleSeries[s.key] ? s.color : '#475569' }} />
        <span style={{ color: visibleSeries[s.key] ? '#f8fafc' : '#94a3b8', fontWeight: visibleSeries[s.key] ? "700" : "500" }}>
          {s.label}
        </span>
      </label>
    ))}
  </div>
  {/* Row 2 */}
  <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", flexWrap: "wrap" }}>
    {[
      { key: 'deposits_total', label: 'Deposits', color: '#efeff7ff' },
      { key: 'withdrawals_total', label: 'Withdraws', color: '#fb923c' },
      { key: 'dealers_tips', label: 'Dealer Tips', color: '#06b6d4' },
      { key: 'service_staff_tips', label: 'Staff Tips', color: '#14b8a6' }
    ].map(s => (
      <label key={s.key} style={{ 
        display: "flex", 
        alignItems: "center", 
        gap: "0.3rem", 
        cursor: "pointer", 
        fontSize: "0.7rem", 
        background: visibleSeries[s.key] ? s.color + '20' : "#0f172a", 
        padding: "0.2rem 0.6rem", 
        borderRadius: "9999px", 
        border: `1px solid ${visibleSeries[s.key] ? s.color : '#334155'}`,
        transition: "all 0.2s"
      }}>
        <input 
          type="checkbox" 
          checked={visibleSeries[s.key]} 
          onChange={() => setVisibleSeries(prev => ({ ...prev, [s.key]: !prev[s.key] }))}
          style={{ display: "none" }}
        />
        <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: visibleSeries[s.key] ? s.color : '#475569' }} />
        <span style={{ color: visibleSeries[s.key] ? '#f8fafc' : '#94a3b8', fontWeight: visibleSeries[s.key] ? "700" : "500" }}>
          {s.label}
        </span>
      </label>
    ))}
  </div>

                    </>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", color: "#f8fafc", fontSize: "0.875rem", fontWeight: "700" }}>
                      <button
                        className="game-icon-btn game-icon-btn--sm"
                        onClick={() => selectCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
                        aria-label="Previous month"
                      >
                        <FaChevronLeft />
                      </button>
                      <span style={{ minWidth: "140px", textAlign: "center" }}>
                        {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                      <button
                        className="game-icon-btn game-icon-btn--sm"
                        onClick={() => selectCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
                        aria-label="Next month"
                      >
                        <FaChevronRight />
                      </button>
                    </div>
                  )}
</div>
              </div>
              
              {performanceView === 'graph' ? (
                <div>
                  <div style={{ height: "300px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                    {usePerformanceBarChart ? (
                      <BarChart data={performanceTrendData} barGap={6} barCategoryGap="18%">
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                        <XAxis 
                          dataKey="session_axis_label" 
                          tick={<SessionDayTick totalTicks={performanceTrendData.length} />}
                          interval={0}
                          height={58}
                          tickMargin={8}
                        />
                        <YAxis tick={{fill: '#94a3b8', fontSize: 10}} />
                        {performanceSeries.map((series) => (
                          visibleSeries[series.key] && (
                            <Bar
                              key={series.key}
                              dataKey={series.key}
                              fill={series.color}
                              name={series.name}
                              radius={[4, 4, 0, 0]}
                              maxBarSize={42}
                            />
                          )
                        ))}
                      </BarChart>
                    ) : (
                      <AreaChart data={performanceTrendData}>
                        <defs>
                          <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                        <XAxis 
                          dataKey="session_axis_label" 
                          tick={<SessionDayTick totalTicks={performanceTrendData.length} />}
                          interval={0}
                          height={58}
                          tickMargin={8}
                        />
                        <YAxis tick={{fill: '#94a3b8', fontSize: 10}} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc', borderRadius: '8px', padding: '12px', border: "1px solid #475569" }}
                          itemStyle={{ fontSize: '12px', padding: "2px 0" }}
                          labelFormatter={(label, payload) => payload?.[0]?.payload?.session_tooltip_label || label}
                          formatter={(value) => `$${parseFloat(value).toLocaleString()}`}
                        />
                        {performanceSeries.map((series) => (
                          visibleSeries[series.key] && (
                            <Area
                              key={series.key}
                              type="monotone"
                              dataKey={series.key}
                              stroke={series.color}
                              fillOpacity={series.areaFill ? 1 : 0}
                              fill={series.areaFill || series.color}
                              strokeWidth={series.strokeWidth || 2}
                              name={series.name}
                            />
                          )
                        ))}
                      </AreaChart>
                    )}
                    </ResponsiveContainer>
                  </div>
                  {usePerformanceBarChart && (
                    <div style={{ display: "flex", justifyContent: "center", gap: "0.75rem", flexWrap: "wrap", marginTop: "0.85rem" }}>
                      {performanceSeries
                        .filter((series) => visibleSeries[series.key])
                        .map((series) => {
                          const value = Number(performanceTrendData[0]?.[series.key]) || 0;
                          return (
                            <div
                              key={series.key}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.4rem",
                                padding: "0.35rem 0.65rem",
                                borderRadius: "999px",
                                background: "#0f172a",
                                border: `1px solid ${series.color}`,
                                color: "#f8fafc",
                                fontSize: "0.78rem",
                                fontWeight: 700,
                              }}
                            >
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: series.color }} />
                              <span>{series.name}</span>
                              <span style={{ color: series.color }}>{formatCurrency(value)}</span>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              ) : (
                <PerformanceCalendar days={calendarDays} />
              )}
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          background: "#1e293b",
          padding: "1.5rem",
          borderRadius: "1rem",
          border: "1px solid #334155",
          marginBottom: "2rem",
        }}
      >
        <div
          onClick={() => setShowBookChanges((open) => !open)}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: showBookChanges ? "1.5rem" : "0",
            padding: "0.5rem",
            borderRadius: "0.5rem",
            transition: "all 0.2s",
            cursor: "pointer",
          }}
          className="hover-bg"
        >
          <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <BookOpen size={20} color="#fbbf24" /> Book Changes
            <span style={{ color: "#94a3b8", fontSize: "0.875rem", fontWeight: 600 }}>
              ({bookChanges?.filter(b => b.net_change !== 0).length || 0} players, {walletChanges?.filter(w => w.old_balance !== w.new_balance).length || 0} wallets)
            </span>
          </h3>
          <div style={{ color: "#94a3b8" }}>
            {showBookChanges ? <FaChevronUp /> : <FaChevronDown />}
          </div>
        </div>

        {showBookChanges && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: "1.5rem" }}>
          {/* Wallet Changes */}
          <div style={{ background: "#1e293b", padding: "1.5rem", borderRadius: "1rem", border: "1px solid #334155", order: 3 }}>
            <div style={{ marginBottom: "1.25rem" }}>
              <h3 style={{ margin: 0, fontSize: "1.1rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <CreditCard size={18} color="#10b981" /> Wallet Changes
              </h3>
              <p style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: "0.25rem" }}>
                <span style={{ color: "#f43f5e" }}>Red:</span> Wallet Owes House • <span style={{ color: "#10b981" }}>Green:</span> House Owes Wallet
              </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.75rem" }}>
              {walletChanges?.filter(w => w.old_balance !== w.new_balance).map(w => (
                <div
                  key={w.id}
                  onClick={() => openDashboardHistory("wallet", { value: w.id, label: w.name })}
                  role="button"
                  tabIndex={0}
                  onMouseEnter={() => setHoveredHistoryTarget(`wallet-${w.id}`)}
                  onMouseLeave={() => setHoveredHistoryTarget(null)}
                  onFocus={() => setHoveredHistoryTarget(`wallet-${w.id}`)}
                  onBlur={() => setHoveredHistoryTarget(null)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openDashboardHistory("wallet", { value: w.id, label: w.name });
                    }
                  }}
                  style={{ padding: "0.75rem 1rem", background: "#0f172a", borderRadius: "0.75rem", border: "1px solid #334155", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem", cursor: "pointer", minWidth: 0 }}
                  title="View wallet transaction history"
                >
                  <span style={{ fontWeight: "600", color: hoveredHistoryTarget === `wallet-${w.id}` ? "#60a5fa" : "#f8fafc", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", transition: "color 0.15s ease" }}>{w.name}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", flexShrink: 0 }}>
                    <span style={{ fontWeight: "800", color: w.old_balance <= 0 ? "#10b981" : "#f43f5e" }}>
                      ${Math.abs(w.old_balance)?.toLocaleString()}
                    </span>
                    <span>→</span>
                    <span style={{ fontWeight: "800", color: w.new_balance <= 0 ? "#10b981" : "#f43f5e" }}>
                      ${Math.abs(w.new_balance)?.toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {!walletChanges?.filter(w => w.old_balance !== w.new_balance).length && <p style={{ color: "#64748b", textAlign: "center", fontSize: "0.875rem", padding: "1rem" }}>No balance changes recorded.</p>}
          </div>

        {/* Row 2: Book Changes */}
        <div style={{ background: "#1e293b", padding: "1.5rem", borderRadius: "1rem", border: "1px solid #334155", display: "flex", flexDirection: "column", order: 2 }}>
            <div style={{ marginBottom: "1.25rem" }}>
              <h3 style={{ margin: 0, fontSize: "1.1rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <BookOpen size={18} color="#fbbf24" /> Book Changes
              </h3>
              <p style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: "0.25rem" }}>
                <span style={{ color: "#f43f5e" }}>Red</span> is Player Debt • <span style={{ color: "#10b981" }}>Green</span> is Player Balance
              </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.75rem", overflowY: "auto", flexGrow: 1, maxHeight: "600px", paddingRight: "0.5rem" }}>
              {bookChanges?.filter(b => b.net_change !== 0).map(b => (
                <div
                  key={b.id}
                  onClick={() => openDashboardHistory("player", { value: b.id, label: b.name })}
                  role="button"
                  tabIndex={0}
                  onMouseEnter={() => setHoveredHistoryTarget(`player-${b.id}`)}
                  onMouseLeave={() => setHoveredHistoryTarget(null)}
                  onFocus={() => setHoveredHistoryTarget(`player-${b.id}`)}
                  onBlur={() => setHoveredHistoryTarget(null)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openDashboardHistory("player", { value: b.id, label: b.name });
                    }
                  }}
                  style={{ padding: "0.75rem 1rem", background: "#0f172a", borderRadius: "0.75rem", border: "1px solid #334155", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem", cursor: "pointer", minWidth: 0 }}
                  title="View player transaction history"
                >
                  <span style={{ fontWeight: "700", color: hoveredHistoryTarget === `player-${b.id}` ? "#60a5fa" : "#f8fafc", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", transition: "color 0.15s ease" }}>{b.name}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", flexShrink: 0 }}>
                    <span style={{ fontWeight: "800", color: b.old_book <= 0 ? "#10b981" : "#f43f5e" }}>
                      ${Math.abs(b.old_book)?.toLocaleString()}
                    </span>
                    <span>→</span>
                    <span style={{ fontWeight: "800", color: b.new_book <= 0 ? "#10b981" : "#f43f5e" }}>
                      ${Math.abs(b.new_book)?.toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
              {!bookChanges?.filter(b => b.net_change !== 0).length && <p style={{ color: "#64748b", textAlign: "center", fontSize: "0.875rem", marginTop: "2rem" }}>No active book movements.</p>}
            </div>
        </div>
          </div>
        )}
      </div>

      {/* Sessions List Section */}
      <div 
        
        style={{
          background: "#1e293b", 
          padding: "1.5rem", 
          borderRadius: "1rem", 
          border: "1px solid #334155", 
          marginBottom: "2rem",
          
        }}>
          <div 
            onClick={() => setShowSessions(!showSessions)} 
            style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center", 
              marginBottom: showSessions ? "1.5rem" : "0",
              padding: "0.5rem",
              borderRadius: "0.5rem",
              transition: "all 0.2s",
              cursor: "pointer"
            }}
            className="hover-bg"
          >
            <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <Calendar size={20} color="#3182ce" /> Sessions List ({sessions.length})
              {showSessions && sessions.length > 0 && (
                <FaFileCsv
                  className="game-icon-btn game-icon-btn--success"
                  onClick={(e) => {
                    e.stopPropagation();
                    const data = sessions.map((s) => ({
                      Tables: s.tables_count || 0,
                      Players: s.players_count || 0,
                      Dealers: s.dealers_count || 0,
                      Rake: s.rake || 0,
                      "Dealer Tips": s.dealers_tips || 0,
                      "Staff Tips": s.service_staff_tips || 0,
                      "Deposits Count": s.deposits_count || 0,
                      "Deposits Total": s.deposits_total || 0,
                      "Withdrawals Count": s.withdrawals_count || 0,
                      "Withdrawals Total": s.withdrawals_total || 0,
                      "Avg Deposit": s.deposits_average || 0,
                      "Avg Withdrawal": s.withdrawals_average || 0,
                      "Debt Gained": s.debt_gained || 0,
                      "Balance Gained": s.balance_gained || 0,
                      "Ending Chips": s.ending_table_chips || 0,
                      "Ending Cash": s.ending_cash || 0,
                      "Max Credit": s.max_total_credit || 0,
                      "Bonuses Count": s.bonuses_count || 0,
                      "Bonuses Total": s.bonuses_total || 0,
                      Costs: s.costs || 0,
                      "Cash Profit": s.cash_game_profit || 0,
                      "Tournament Profit": s.tournament_profit || 0,
                      "Total Profit": s.total_profit || 0,
                    }));
                    exportToCSV(`Sessions_List_${timeframe}`, Object.keys(data[0] || {}), data, e);
                  }}
                />
              )}
            </h3>
            <div style={{ color: "#94a3b8" }}>
              {showSessions ? <FaChevronUp /> : <FaChevronDown />}
            </div>
          </div>
          {showSessions && (
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {sessions?.slice(0, 15).map(session => {
              const isOpen = openSessions[session.id];
              return (
                <div key={session.id} className="game-table-card" style={{ border: isOpen ? "2px solid #3182ce" : "1px solid #334155", borderRadius: "12px", background: "#0f172a", overflow: "hidden", transition: "all 0.3s ease" }}>
                    <div
                      className="game-table-header"
                      onClick={() => toggleSession(session.id)}
                      style={{
                        cursor: "pointer",
                        padding: "1rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background: isOpen ? "rgba(255, 255, 255, 0.05)" : "transparent",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "1.5rem", flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: "1rem", fontWeight: 700, whiteSpace: "nowrap", color: "#f8fafc" }}>
                          {formatDate(session.start_time)}{" "}
                          {formatTime(session.start_time)}
                        </span>
                        <span style={{ color: "#94a3b8", fontSize: "0.85rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {session.tables_count} tables • {session.players_count} players • {session.dealers_count} dealers
                        </span>
                      </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginLeft: '1rem' }}>
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: "1.1rem",
                          background: "rgba(0,0,0,0.3)",
                          padding: "0.2rem 0.6rem",
                          borderRadius: "4px",
                          textAlign: "center",
                          color: session.total_profit >= 0 ? "#10b981" : "#f43f5e"
                        }}
                      >
                        {formatCurrency(session.total_profit || 0)}
                      </span>
                      {isOpen ? <FaChevronDown style={{ color: "#94a3b8", fontSize: "0.9rem" }} /> : <FaChevronRight style={{ color: "#94a3b8", fontSize: "0.9rem" }} />}
                    </div>
                  </div>

                  {isOpen && (
                    <div style={{ padding: '1.5rem', background: "rgba(255, 255, 255, 0.02)", borderTop: "1px solid #334155" }}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                          gap: "1.5rem",
                          marginBottom: "2rem",
                          padding: "1.5rem",
                          background: "rgba(0,0,0,0.15)",
                          borderRadius: "8px",
                          border: "1px solid rgba(255,255,255,0.05)",
                        }}
                      >
                        {[
                          { label: "RAKE", value: formatCurrency(session.rake || 0) },
                          { label: "DEALER TIPS", value: formatCurrency(session.dealers_tips || 0) },
                          { label: "STAFF TIPS", value: formatCurrency(session.service_staff_tips || 0) },
                          { label: "DEPOSITS", value: `${session.deposits_count || 0} (${formatCurrency(session.deposits_total || 0)})` },
                          { label: "WITHDRAWALS", value: `${session.withdrawals_count || 0} (${formatCurrency(session.withdrawals_total || 0)})` },
                          { label: "PLAYING TIME", value: session.total_playing_time || "0:00" },
                          { label: "AVG DEPOSIT", value: formatCurrency(session.deposits_average || 0) },
                          { label: "AVG WITHDRAWAL", value: formatCurrency(session.withdrawals_average || 0) },
                          { label: "DEBT GAINED", value: formatCurrency(session.debt_gained || 0), color: (session.debt_gained || 0) > 0 ? "#f43f5e" : "" },
                          { label: "BALANCE GAINED", value: formatCurrency(session.balance_gained || 0), color: (session.balance_gained || 0) > 0 ? "#10b981" : "" },
                          { label: "ENDING CHIPS", value: formatCurrency(session.ending_table_chips || 0), color: (session.ending_table_chips || 0) < 0 ? "#f43f5e" : "" },
                          { label: "ENDING CASH", value: formatCurrency(session.ending_cash || 0) },
                          { label: "MAX CREDIT", value: formatCurrency(session.max_total_credit || 0) },
                          { label: "BONUSES", value: `${session.bonuses_count || 0} (${formatCurrency(session.bonuses_total || 0)})` },
                          { label: "COSTS", value: formatCurrency(session.costs || 0), color: (session.costs || 0) !== 0 ? "#f43f5e" : "" },
                          { label: "CASH PROFIT", value: formatCurrency(session.cash_game_profit || 0), color: (session.cash_game_profit || 0) >= 0 ? "#10b981" : "#f43f5e" },
                          { label: "TOURNAMENT PROFIT", value: formatCurrency(session.tournament_profit || 0), color: (session.tournament_profit || 0) >= 0 ? "#10b981" : "#f43f5e" },
                        ].map((stat, i) => (
                          <div key={i}>
                            <div style={{ fontSize: "0.75rem", marginBottom: "0.25rem", fontWeight: 700, letterSpacing: "0.05em", color: "#64748b" }}>{stat.label}</div>
                            <div style={{ fontSize: "1.1rem", fontWeight: 700, color: stat.color || "#f8fafc" }}>{stat.value}</div>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
                        <button className={`game-btn ${activeSessionView[session.id] === 'tables' ? 'game-btn--primary' : 'game-btn--ghost'}`} onClick={() => showTables(session.id)} style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }}>Tables</button>
                        <button className={`game-btn ${activeSessionView[session.id] === 'tournaments' ? 'game-btn--primary' : 'game-btn--ghost'}`} onClick={() => showTournaments(session.id)} style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }}>Tournaments</button>
                        <button className="game-btn game-btn--ghost" onClick={() => { setSelectedSessionId(session.id); loadSessionTransactions(session.id); }} style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }}>Payouts</button>
                        <button className="game-btn game-btn--ghost" onClick={() => listSessionCashTransactions(session.id)} style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }}>Cash</button>
                        <button className="game-btn game-btn--ghost" onClick={() => { setShowBookReportModal(true); setBookReportSessionId(session.id); loadBookChanges(session.id); setBookReportPlayerId(""); setBookReportTransactions(null); }} style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }}>Book</button>
                        <button className="game-btn game-btn--ghost" onClick={() => { setShowWalletReportModal(true); setWalletReportSessionId(session.id); loadWalletChanges(session.id); }} style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }}>Wallets</button>
                        <button className="game-btn game-btn--ghost" onClick={() => { setShowBonusModal(true); setBonusSessionId(session.id); loadBonusTransactions(session.id); }} style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }}>Bonuses</button>
                      </div>

                      {activeSessionView[session.id] === 'tables' && (
                        <div style={{ padding: "1rem", background: "#0f172a", borderRadius: "8px", border: "1px solid #334155" }}>
                          <h4 style={{ fontSize: "0.875rem", fontWeight: "700", marginBottom: "1rem", color: "#3182ce" }}>SESSION TABLES</h4>
                          {!sessionTables[session.id] ? (
                            <div style={{ color: "#64748b", fontSize: "0.875rem" }}>Loading tables...</div>
                          ) : sessionTables[session.id].length === 0 ? (
                            <div style={{ color: "#64748b", fontSize: "0.875rem" }}>No tables recorded.</div>
                          ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                              {sessionTables[session.id].map(t => (
                                <div key={t.id} style={{ background: "#1e293b", padding: "1.25rem", borderRadius: "8px", border: "1px solid #334155" }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
                                        <span style={{ fontWeight: 700, fontSize: "1.2rem", color: "#f8fafc" }}>{t.stakes} {t.game_type}</span>
                                        <button className="game-icon-btn game-icon-btn--info" style={{ marginLeft: "0.5rem" }}
                                          onClick={() => loadTableTransactions(t.id)}><FaInfo/></button>
                                      </div>
                                      <div style={{ fontSize: "0.9rem", color: "#94a3b8", marginBottom: "0.5rem" }}>
                                        {formatTime(t.start_time)} - {t.end_time ? formatTime(t.end_time) : "Active"} • {t.length || "0h 0m"}
                                      </div>
                                      <div style={{ fontSize: "0.85rem", color: "#64748b" }}>
                                        {t.total_players_count || 0} players &nbsp;•&nbsp; {t.deposits_count || 0} deposits ({formatCurrency(t.deposits_total || 0)}) &nbsp;•&nbsp; {t.withdrawals_count || 0} withdrawals ({formatCurrency(t.withdrawals_total || 0)})
                                      </div>
                                    </div>
                                    <div style={{ textAlign: "right" }}>
                                      <div style={{ fontWeight: 700, fontSize: "1.15rem", color: "#10b981", marginBottom: "0.25rem" }}>{formatCurrency(t.rake)} rake</div>
                                      <div style={{ fontSize: "1rem", color: "#94a3b8" }}>{formatCurrency(t.tips)} tips</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {activeSessionView[session.id] === 'tournaments' && (
                        <div style={{ padding: "1rem", background: "#0f172a", borderRadius: "8px", border: "1px solid #334155" }}>
                          <h4 style={{ fontSize: "0.875rem", fontWeight: "700", marginBottom: "1rem", color: "#3182ce" }}>SESSION TOURNAMENTS</h4>
                          {!sessionTournaments[session.id] ? (
                            <div style={{ color: "#64748b", fontSize: "0.875rem" }}>Loading tournaments...</div>
                          ) : sessionTournaments[session.id].length === 0 ? (
                            <div style={{ color: "#64748b", fontSize: "0.875rem" }}>No tournaments recorded.</div>
                          ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                              {sessionTournaments[session.id].map(t => (
                                <div key={t.id} style={{ background: "#1e293b", padding: "1.25rem", borderRadius: "8px", border: "1px solid #334155" }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
                                        <span style={{ fontWeight: 700, fontSize: "1.2rem", color: "#f8fafc" }}>{t.name}</span>
                                        <span style={{ fontSize: "0.75rem", background: t.state === 'finished' ? "#334155" : "#10b981", color: "white", padding: "2px 8px", borderRadius: "4px" }}>{t.state}</span>
                                      </div>
                                      <div style={{ fontSize: "0.95rem", color: "#94a3b8" }}>
                                        {t.total_entries || 0} entries • {t.rebuys_num || 0} rebuys • {t.addons_num || 0} addons
                                      </div>
                                    </div>
                                    <div style={{ textAlign: "right" }}>
                                      <div style={{ fontWeight: 700, fontSize: "1.15rem", color: t.profit >= 0 ? "#10b981" : "#f43f5e", marginBottom: "0.25rem" }}>{formatCurrency(t.profit)} profit</div>
                                      <div style={{ fontSize: "1rem", color: "#94a3b8" }}>{formatCurrency(t.total_tips)} tips</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
        )}
      </div>

      {/* Dealer Analytics Section */}
      <div style={{ background: "#1e293b", padding: "1.5rem", borderRadius: "1rem", border: "1px solid #334155", marginBottom: "2rem" }}>
        <div
          onClick={() => setShowDealers(d => !d)}
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showDealers ? "1.5rem" : "0", padding: "0.5rem", borderRadius: "0.5rem", cursor: "pointer", transition: "all 0.2s" }}
          className="hover-bg"
        >
          <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Award size={20} color="#06b6d4" /> Dealer Analytics ({dealerRecords.length})
            {showDealers && dealerRecords.length > 0 && (
              <FaFileCsv
                className="game-icon-btn game-icon-btn--success"
                onClick={(e) => {
                  e.stopPropagation();
                  const data = [];
                  dealerRecords.forEach(dealer => {
                    dealer.breakdowns.forEach(b => {
                      data.push({
                        "Dealer Name": dealer.name,
                        "Table Type": `${b.stakes} ${b.game_type}`,
                        "# of Tables": b.entries_count,
                        "Total Rake": b.total_rake,
                        "Total Tips": b.total_tips,
                        "Rake/Hour": b.avg_rake_hr != null ? b.avg_rake_hr : 0,
                        "Tips/Hour": b.avg_tips_hr != null ? b.avg_tips_hr : 0
                      });
                    });
                  });
                  exportToCSV(`Dealer_Analytics_${timeframe}`, Object.keys(data[0] || {}), data, e);
                }}
              />
            )}
          </h3>
          <div style={{ color: "#94a3b8" }}>{showDealers ? <FaChevronUp /> : <FaChevronDown />}</div>
        </div>

        {showDealers && (
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {dealerRecords.length === 0 ? (
              <p style={{ color: "#64748b", textAlign: "center", fontSize: "0.875rem" }}>No dealer data for this timeframe.</p>
            ) : dealerRecords.map(dealer => {
              const isOpen = openDealers[dealer.dealer_id];
              const totalMins = Math.floor(dealer.total_time_sec / 60);
              const hrs = Math.floor(totalMins / 60);
              const mins = totalMins % 60;
              const timeLabel = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
              return (
                <div key={dealer.dealer_id} style={{ border: isOpen ? "2px solid #06b6d4" : "1px solid #334155", borderRadius: "12px", background: "#0f172a", overflow: "hidden", transition: "all 0.3s ease" }}>
                  {/* Dealer Header */}
                  <div
                    onClick={() => setOpenDealers(prev => ({ ...prev, [dealer.dealer_id]: !prev[dealer.dealer_id] }))}
                    style={{ cursor: "pointer", padding: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between", background: isOpen ? "rgba(255,255,255,0.05)" : "transparent" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "1.5rem", flex: 1 }}>
                      <span style={{ fontSize: "1rem", fontWeight: 700, color: "#f8fafc" }}>{dealer.name}</span>
                      <span style={{ color: "#94a3b8", fontSize: "0.85rem" }}>
                        {timeLabel} dealt &nbsp;•&nbsp; ${dealer.total_rake.toLocaleString()} rake &nbsp;•&nbsp; ${dealer.total_tips.toLocaleString()} tips
                      </span>
                    </div>
                    <div style={{ color: "#94a3b8", fontSize: "0.9rem" }}>
                      {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                    </div>
                  </div>

                  {/* Breakdowns */}
                  {isOpen && (
                    <div style={{ padding: "1rem 1.5rem 1.5rem", borderTop: "1px solid #334155", background: "rgba(255,255,255,0.02)" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                        {dealer.breakdowns.map((b, i) => {
                          const bMins = Math.floor(b.total_time_sec / 60);
                          const bHrs = Math.floor(bMins / 60);
                          const bRemMins = bMins % 60;
                          const bTimeLabel = bHrs > 0 ? `${bHrs}h ${bRemMins}m` : bMins > 0 ? `${bRemMins}m` : null;
                          return (
                            <div key={i} style={{ background: "#1e293b", borderRadius: "8px", border: "1px solid #334155", padding: "1rem 1.25rem", display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: "1rem" }}>
                              <div>
                                <div style={{ fontWeight: 700, fontSize: "1rem", color: "#f8fafc", marginBottom: "0.25rem" }}>
                                  {b.stakes} {b.game_type}
                                </div>
                                <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                                  {b.entries_count} {b.entries_count === 1 ? "table" : "tables"}
                                  {bTimeLabel && <span style={{ marginLeft: "0.5rem" }}>• {bTimeLabel} dealt</span>}
                                </div>
                              </div>
                              <div style={{ display: "flex", gap: "2rem", textAlign: "right" }}>
                                <div>
                                  <div style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 700, letterSpacing: "0.05em", marginBottom: "0.2rem" }}>TOTAL RAKE</div>
                                  <div style={{ fontWeight: 700, color: "#10b981" }}>${b.total_rake.toLocaleString()}</div>
                                  <div style={{ fontSize: "0.75rem", color: "#b4bdbfff" }}>
                                    {b.avg_rake_hr != null ? `$${b.avg_rake_hr}/hr` : ""}
                                  </div>
                                </div>
                                <div>
                                  <div style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 700, letterSpacing: "0.05em", marginBottom: "0.2rem" }}>TOTAL TIPS</div>
                                  <div style={{ fontWeight: 700, color: "#06b6d4" }}>${b.total_tips.toLocaleString()}</div>
                                  <div style={{ fontSize: "0.75rem", color: "#b4bdbfff" }}>
                                    {b.avg_tips_hr != null ? `$${b.avg_tips_hr}/hr` : ""}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}

                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Player Analytics Table */}
      <div style={{ background: "#1e293b", padding: "1.5rem", borderRadius: "1rem", border: "1px solid #334155", marginBottom: "2rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Users size={20} color="#3182ce" /> Player Analytics ({sortedPlayers.length})
            {sortedPlayers.length > 0 && (
              <FaFileCsv
                className="game-icon-btn game-icon-btn--success"
                onClick={(e) => {
                  const data = sortedPlayers.map(p => ({
                    Player: p.name,
                    "Play Time": (p.total_time_sec / 3600).toFixed(1) + 'h',
                    Sessions: p.sessions_played,
                    Deposits: p.total_deposits,
                    Withdraws: p.total_withdrawals,
                    Bonuses: p.total_bonuses,
                    "Debt Gained": p.total_debt_gained,
                    "Bal Gained": p.total_balance_gained,
                    "Tourn. Profit": p.total_tournament_profit,
                    "Cash Profit": p.total_cash_profit,
                    Combined: p.total_profit
                  }));
                  exportToCSV(`Player_Analytics_${timeframe}`, Object.keys(data[0] || {}), data, e);
                }}
              />
            )}
          </h3>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
            <thead>
              <tr style={{ textAlign: "left", color: "#94a3b8", borderBottom: "1px solid #334155" }}>
                <th onClick={() => requestSort('name')} style={{ padding: "0.75rem", cursor: "pointer" }}>Player {sortConfig.key === 'name' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                <th onClick={() => requestSort('total_time_sec')} style={{ padding: "0.75rem", cursor: "pointer" }}>Play Time {sortConfig.key === 'total_time_sec' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                <th onClick={() => requestSort('sessions_played')} style={{ padding: "0.75rem", cursor: "pointer" }}>Sessions {sortConfig.key === 'sessions_played' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                <th onClick={() => requestSort('total_deposits')} style={{ padding: "0.75rem", cursor: "pointer" }}>Deposits {sortConfig.key === 'total_deposits' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                <th onClick={() => requestSort('total_withdrawals')} style={{ padding: "0.75rem", cursor: "pointer" }}>Withdraws {sortConfig.key === 'total_withdrawals' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                <th onClick={() => requestSort('total_bonuses')} style={{ padding: "0.75rem", cursor: "pointer" }}>Bonuses {sortConfig.key === 'total_bonuses' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                <th onClick={() => requestSort('total_debt_gained')} style={{ padding: "0.75rem", cursor: "pointer" }}>Debt Gained {sortConfig.key === 'total_debt_gained' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                <th onClick={() => requestSort('total_balance_gained')} style={{ padding: "0.75rem", cursor: "pointer" }}>Bal Gained {sortConfig.key === 'total_balance_gained' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                <th onClick={() => requestSort('total_tournament_profit')} style={{ padding: "0.75rem", cursor: "pointer" }}>Tourn. Profit {sortConfig.key === 'total_tournament_profit' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                <th onClick={() => requestSort('total_cash_profit')} style={{ padding: "0.75rem", cursor: "pointer" }}>Cash Profit {sortConfig.key === 'total_cash_profit' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
                <th onClick={() => requestSort('total_profit')} style={{ padding: "0.75rem", cursor: "pointer", color: "#3182ce" }}>Combined {sortConfig.key === 'total_profit' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
              </tr>
            </thead>
            <tbody>
              {sortedPlayers?.map(p => (
                <tr key={p.player_id} style={{ borderBottom: "1px solid #0f172a" }}>
                  <td style={{ padding: "0.75rem" }}>
                    <span
                      onClick={() => openDashboardHistory("player", { value: p.player_id, label: p.name })}
                      role="button"
                      tabIndex={0}
                      onMouseEnter={() => setHoveredHistoryTarget(`analytics-player-${p.player_id}`)}
                      onMouseLeave={() => setHoveredHistoryTarget(null)}
                      onFocus={() => setHoveredHistoryTarget(`analytics-player-${p.player_id}`)}
                      onBlur={() => setHoveredHistoryTarget(null)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openDashboardHistory("player", { value: p.player_id, label: p.name });
                        }
                      }}
                      style={{
                        color: hoveredHistoryTarget === `analytics-player-${p.player_id}` ? "#60a5fa" : "#f8fafc",
                        cursor: "pointer",
                        fontWeight: "600",
                        transition: "color 0.15s ease",
                      }}
                      title="View player transaction history"
                    >
                      {p.name}
                    </span>
                  </td>
                  <td style={{ padding: "0.75rem" }}>{(p.total_time_sec / 3600).toFixed(1)}h</td>
                  <td style={{ padding: "0.75rem" }}>{p.sessions_played?.toLocaleString()}</td>
                  <td style={{ padding: "0.75rem", color: "#10b981" }}>${p.total_deposits?.toLocaleString()}</td>
                  <td style={{ padding: "0.75rem", color: "#f43f5e" }}>${p.total_withdrawals?.toLocaleString()}</td>
                  <td style={{ padding: "0.75rem", color: "#fbbf24" }}>${p.total_bonuses?.toLocaleString()}</td>
                  <td style={{ padding: "0.75rem", color: p.total_debt_gained > 0 ? "#f43f5e" : "#94a3b8" }}>${p.total_debt_gained?.toLocaleString()}</td>
                  <td style={{ padding: "0.75rem", color: p.total_balance_gained > 0 ? "#10b981" : "#94a3b8" }}>${p.total_balance_gained?.toLocaleString()}</td>
                  <td style={{ padding: "0.75rem", color: p.total_tournament_profit >= 0 ? "#10b981" : "#f43f5e" }}>${p.total_tournament_profit?.toLocaleString()}</td>
                  <td style={{ padding: "0.75rem", color: p.total_cash_profit >= 0 ? "#10b981" : "#f43f5e" }}>${p.total_cash_profit?.toLocaleString()}</td>
                  <td style={{ padding: "0.75rem", fontWeight: "700", color: p.total_profit >= 0 ? "#10b981" : "#f43f5e" }}>${p.total_profit?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {/* Modals - Integrated from Data.jsx style */}
      {selectedTableId !== null && (
        <>
          <div className="game-modal-overlay" onClick={() => { setSelectedTableId(null); setSelectedTableTransactions([]); }} />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                Table Transactions
                {selectedTableTransactions.length > 0 && (
                  <FaFileCsv
                    className="game-icon-btn game-icon-btn--success"
                    onClick={(e) => {
                      const filtered = getFilteredTableTransactions();
                      const data = filtered.map(t => ({
                        Time: formatTime(t.completed_at),
                        Player: t.player_or_dealer || t.player_name || "",
                        Amount: t.amount,
                        Type: formatTransactionCode(t.code, t.type, t.method),
                        Method: t.method || "",
                        Date: formatDate(t.completed_at)
                      }));
                      exportToCSV("Table_Transactions", Object.keys(data[0] || {}), data, e);
                    }}
                  />
                )}
              </h3>
              <button className="game-icon-btn" onClick={() => { setSelectedTableId(null); setSelectedTableTransactions([]); }}><FaTimes /></button>
            </div>
            <div className="game-modal__filters">
              {[{ key: "rake", label: "Rake" }, { key: "tips", label: "Tips" }, { key: "deposit", label: "Deposits" }, { key: "withdrawal", label: "Withdrawals" }, { key: "book", label: "Books" }].map(({ key, label }) => (
                <label key={key} className="game-filter-chip">
                  <input type="checkbox" checked={txFilters[key]} onChange={(e) => setTxFilters((prev) => ({ ...prev, [key]: e.target.checked }))} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <div className="game-modal__body game-modal__body--scroll">
              {(() => {
                const filtered = getFilteredTableTransactions();
                if (filtered.length === 0) return <p className="game-empty">No matching transactions.</p>;
                return (
                  <div className="game-transaction-list">
                    {filtered.map((t) => (
                      <div key={t.id} className="game-transaction-item">
                        <span className="game-transaction-time">{formatTime(t.completed_at)}</span>
                        <strong className="game-transaction-player">{t.player_or_dealer || t.player_name || ""}</strong>
                        <span className={`game-transaction-amount ${t.type === "withdrawal" ? "game-text-danger" : "game-text-success"}`}>${Math.abs(parseFloat(t.amount)).toFixed(0)}</span>
                        <span className="game-transaction-type">{formatTransactionCode(t.code, t.type, t.method)}</span>
                        <span className="game-transaction-method">{t.method}</span>
                        <span className="game-transaction-date">{formatDate(t.completed_at)}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* Book Report Modal */}
      {showBookReportModal && (
        <>
          <div className="game-modal-overlay" onClick={() => { setShowBookReportModal(false); setBookReportSessionId(null); setBookReportPlayerId(""); setBookReportTransactions(null); }} />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Book Report - {formatDate(sessions.find((s) => s.id === bookReportSessionId)?.start_time)}
              </h3>
              <button className="game-icon-btn" onClick={() => { setShowBookReportModal(false); setBookReportSessionId(null); setBookReportPlayerId(""); setBookReportTransactions(null); }}><FaTimes /></button>
            </div>

            <div style={{ padding: "1.5rem", borderBottom: "1px solid var(--game-border)", maxWidth: "50%", width: "100%" }}>
              <div style={{ display: "flex", gap: "1rem" }}>
                <div style={{ flex: 1 }}>
                  <Select
                    options={bookReportChanges.map((c) => ({ value: c.player_id, label: c.player_name }))}
                    isSearchable
                    placeholder="Select Player"
                    className="game-select"
                    classNamePrefix="select"
                    styles={{
                      control: (base) => ({ ...base, backgroundColor: "#2d3748", borderColor: "#4a5568", color: "#e2e8f0" }),
                      menu: (base) => ({ ...base, backgroundColor: "#2d3748" }),
                      option: (base, { isFocused }) => ({ ...base, backgroundColor: isFocused ? "#4a5568" : "#2d3748", color: "#e2e8f0" }),
                      singleValue: (base) => ({ ...base, color: "#e2e8f0" }),
                    }}
                    onChange={(opt) => setBookReportPlayerId(opt ? opt.value : "")}
                    value={bookReportChanges.map((c) => ({ value: c.player_id, label: c.player_name })).find((o) => o.value === bookReportPlayerId) || null}
                  />
                </div>
                {bookReportPlayerId && (
                  <button className="game-icon-btn" onClick={() => { setBookReportPlayerId(""); setBookReportTransactions(null); }} title="Clear Selection">
                    <FaTimes />
                  </button>
                )}
                <button className="game-btn game-btn--primary" onClick={() => searchBookReport()} disabled={!bookReportPlayerId}>Search</button>
              </div>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {bookReportPlayerId && (
                <div style={{ marginBottom: "2rem" }}>
                  <h4 style={{ marginBottom: "1rem" }}>Transactions</h4>
                  {bookReportTransactions === null ? <p className="game-empty">Click Search to load transactions</p> : bookReportTransactions.length === 0 ? <p className="game-empty">No transactions found.</p> : (
                    <div className="game-transaction-list">
                      {bookReportTransactions.map((t) => {
                        const amount = parseFloat(t.amount || 0);
                        // Increase Debt (Red): 1, 8, 9, 17, 18, 30
                        // Decrease Debt (Green): 4, 7, 10, 11, 12, 13, 14, 15, 16, 31
                        const isDecrease = [4, 7, 10, 11, 12, 13, 14, 15, 16, 31].includes(t.code);
                        const title = formatTransactionCode(t.code, t.type, t.method);
                        
                        return (
                          <div key={t.id} className="game-transaction-item" style={{ 
                            background: isDecrease ? "rgba(72, 187, 120, 0.08)" : "rgba(245, 101, 101, 0.08)",
                            border: `1px solid ${isDecrease ? "rgba(72, 187, 120, 0.2)" : "rgba(245, 101, 101, 0.2)"}`,
                            padding: "1rem 1.25rem"
                          }}>
                            <span className="game-transaction-time" style={{ width: "80px" }}>{formatTime(t.completed_at)}</span>
                            <span className="game-transaction-amount" style={{ width: "70px", fontSize: "1.1rem", fontWeight: 800, color: isDecrease ? "var(--game-success)" : "var(--game-danger)" }}>${Math.abs(amount).toFixed(0)}</span>
                            
                            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "0.75rem" }}>
                              <strong style={{ fontSize: "1rem" }}>{title}</strong>
                              {(t.receiver_name || t.name) && <span style={{ color: "var(--game-text-muted)" }}>{t.receiver_name || t.name}</span>}
                            </div>

                            <span className="game-transaction-date" style={{ width: "100px", textAlign: "right", color: "var(--game-text-muted)" }}>{formatDate(t.completed_at)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {bookReportChanges.length > 0 && !bookReportPlayerId && (
                <div style={{ borderTop: "none", pt: "0" }}>
                  <h4 style={{ marginBottom: "1rem", marginTop: "0" }}>Book Changes For Session</h4>
                  <div className="game-transaction-list">
                    {bookReportChanges.map((change) => (
                      <div key={change.player_id} className="game-transaction-item" style={{ display: "flex", alignItems: "center", padding: "1rem", justifyContent: "space-between" }}>
                        <div style={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
                          <strong style={{ cursor: "pointer" }} onClick={() => { setBookReportPlayerId(change.player_id); searchBookReport(change.player_id); }}>{change.player_name}</strong>
                        </div>

                        <div style={{ flex: 2, display: "flex", justifyContent: "center", gap: "2rem", alignItems: "center" }}>
                          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                            <span className="game-text-muted" style={{ fontSize: "0.8rem" }}>Old:</span>
                            <span className={`game-player-badge ${parseFloat(change.old_book || 0) > 0 ? "game-player-badge--debt" : "game-player-badge--credit"}`}>
                              ${Math.abs(parseFloat(change.old_book || 0)).toFixed(0)}
                            </span>
                          </div>
                          <span style={{ fontWeight: 700, color: "var(--game-text-muted)" }}>→</span>
                          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                            <span className="game-text-muted" style={{ fontSize: "0.8rem" }}>Updated:</span>
                            <span className={`game-player-badge ${parseFloat(change.current_book || 0) > 0 ? "game-player-badge--debt" : "game-player-badge--credit"}`}>
                              ${Math.abs(parseFloat(change.current_book || 0)).toFixed(0)}
                            </span>
                          </div>
                        </div>
                        <div style={{ flex: 1 }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Wallet Report Modal */}
      {showWalletReportModal && (
        <>
          <div className="game-modal-overlay" onClick={() => { setShowWalletReportModal(false); setWalletReportSessionId(null); setWalletReportWalletId(""); setWalletReportTransactions(null); }} />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Wallet Changes - {formatDate(sessions.find((s) => s.id === walletReportSessionId)?.start_time)}
              </h3>
              <button className="game-icon-btn" onClick={() => { setShowWalletReportModal(false); setWalletReportSessionId(null); setWalletReportWalletId(""); setWalletReportTransactions(null); }}><FaTimes /></button>
            </div>

            <div style={{ padding: "1.5rem", borderBottom: "1px solid var(--game-border)", maxWidth: "50%", width: "100%" }}>
              <div style={{ display: "flex", gap: "1rem" }}>
                <div style={{ flex: 1 }}>
                  <Select
                    options={walletReportChanges.map((w) => ({ value: w.wallet_id, label: w.wallet_name }))}
                    isSearchable
                    placeholder="Select Wallet"
                    className="game-select"
                    classNamePrefix="select"
                    styles={{
                      control: (base) => ({ ...base, backgroundColor: "#2d3748", borderColor: "#4a5568", color: "#e2e8f0" }),
                      menu: (base) => ({ ...base, backgroundColor: "#2d3748" }),
                      option: (base, { isFocused }) => ({ ...base, backgroundColor: isFocused ? "#4a5568" : "#2d3748", color: "#e2e8f0" }),
                      singleValue: (base) => ({ ...base, color: "#e2e8f0" }),
                    }}
                    onChange={(opt) => setWalletReportWalletId(opt ? opt.value : "")}
                    value={walletReportChanges.map((w) => ({ value: w.wallet_id, label: w.wallet_name })).find((o) => o.value === walletReportWalletId) || null}
                  />
                </div>
                {walletReportWalletId && (
                  <button className="game-icon-btn" onClick={() => { setWalletReportWalletId(""); setWalletReportTransactions(null); }} title="Clear Selection">
                    <FaTimes />
                  </button>
                )}
                <button className="game-btn game-btn--primary" onClick={() => searchWalletReport()} disabled={!walletReportWalletId}>Search</button>
              </div>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {walletReportWalletId && (
                <div style={{ marginBottom: "2rem" }}>
                  <h4 style={{ marginBottom: "1rem" }}>Transactions</h4>
                  {walletReportTransactions === null ? <p className="game-empty">Click Search to load transactions</p> : walletReportTransactions.length === 0 ? <p className="game-empty">No transactions found.</p> : (
                    <div className="game-transaction-list">
                      {walletReportTransactions.map((t) => {
                        const amount = parseFloat(t.amount || 0);
                        const code = Number(t.code);
                        const isIncrease = WALLET_INCREASE_CODES.includes(code);
                        const isDecrease = WALLET_DECREASE_CODES.includes(code);
                        const title = formatTransactionCode(t.code, t.source, t.method);

                        return (
                          <div key={t.id} className="game-transaction-item" style={{ 
                            background: isIncrease ? "rgba(72, 187, 120, 0.08)" : isDecrease ? "rgba(245, 101, 101, 0.08)" : "transparent",
                            border: `1px solid ${isIncrease ? "rgba(72, 187, 120, 0.2)" : isDecrease ? "rgba(245, 101, 101, 0.2)" : "var(--game-border)"}`,
                            padding: "1rem 1.25rem"
                          }}>
                            <span className="game-transaction-time" style={{ width: "80px" }}>{formatTime(t.completed_at)}</span>
                            <span className="game-transaction-amount" style={{ width: "70px", fontSize: "1.1rem", fontWeight: 800, color: isIncrease ? "var(--game-success)" : isDecrease ? "var(--game-danger)" : "var(--game-text-primary)" }}>${Math.abs(amount).toFixed(0)}</span>
                            
                            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "0.75rem" }}>
                              <strong style={{ fontSize: "1rem" }}>{title}</strong>
                              {t.entity_name && <span style={{ color: "var(--game-text-muted)" }}>{t.entity_name}</span>}
                            </div>

                            <span className="game-transaction-date" style={{ width: "100px", textAlign: "right", color: "var(--game-text-muted)" }}>{formatDate(t.completed_at)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {walletReportChanges.length > 0 && !walletReportWalletId && (
                <div style={{ borderTop: "none", pt: "0" }}>
                  <h4 style={{ marginBottom: "1rem", marginTop: "0" }}>Wallet Changes For Session</h4>
                  <div className="game-transaction-list">
                    {walletReportChanges.map((change) => (
                      <div key={change.wallet_id} className="game-transaction-item" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.75rem 1rem" }}>
                        <strong style={{ cursor: "pointer", minWidth: "150px", textAlign: "left" }} onClick={() => { setWalletReportWalletId(change.wallet_id); searchWalletReport(change.wallet_id); }}>
                          {change.wallet_name}
                        </strong>
                        <div style={{ display: "flex", flex: 1, justifyContent: "center", gap: "1.5rem", alignItems: "center" }}>
                          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                            <span className="game-text-muted" style={{ fontSize: "0.8rem" }}>Old:</span>
                            <span className={`game-player-badge ${parseFloat(change.old_balance || 0) > 0 ? "game-player-badge--debt" : "game-player-badge--credit"}`}>
                              ${Math.abs(parseFloat(change.old_balance || 0)).toFixed(0)}
                            </span>
                          </div>
                          <span style={{ fontWeight: 700, color: "var(--game-text-muted)" }}>→</span>
                          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                            <span className="game-text-muted" style={{ fontSize: "0.8rem" }}>Updated:</span>
                            <span className={`game-player-badge ${parseFloat(change.current_balance || 0) > 0 ? "game-player-badge--debt" : "game-player-badge--credit"}`}>
                              ${Math.abs(parseFloat(change.current_balance || 0)).toFixed(0)}
                            </span>
                          </div>
                        </div>
                        <div style={{ minWidth: "150px" }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showHistoryModal && (
        <>
          <div className="game-modal-overlay" onClick={closeDashboardHistory} />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                {historySearchEntity?.label}'s Transaction History
                {historyResults.length > 0 && (
                  <FaFileCsv
                    className="game-icon-btn game-icon-btn--success"
                    onClick={(e) => {
                      const rows = getFilteredHistoryResults().map((tx) => ({
                        "Date/Time": `${formatTime(tx.completed_at)} ${formatDate(tx.completed_at)}`,
                        Action: formatTransactionCode(tx.code),
                        Amount: tx.amount,
                        Method: tx.method || "",
                      }));
                      exportToCSV(
                        `${historySearchEntity?.label}_Transactions`,
                        Object.keys(rows[0] || {}),
                        rows,
                        e,
                      );
                    }}
                  />
                )}
              </h3>
              <button className="game-icon-btn" onClick={closeDashboardHistory}>
                <FaTimes />
              </button>
            </div>

            {historySearchType === "player" && (
              <div className="game-modal__filters">
                {[
                  { key: "deposit", label: "Deposit" },
                  { key: "withdrawal", label: "Withdrawal" },
                  { key: "book", label: "Book" },
                ].map(({ key, label }) => (
                  <label key={key} className="game-filter-chip">
                    <input
                      type="checkbox"
                      checked={historyTxFilters[key]}
                      onChange={(e) =>
                        setHistoryTxFilters((prev) => ({
                          ...prev,
                          [key]: e.target.checked,
                        }))
                      }
                    />
                    <span>{label}</span>
                  </label>
                ))}
                <div className="game-filter-actions">
                  <button
                    type="button"
                    className="game-btn game-btn--sm"
                    onClick={() =>
                      setHistoryTxFilters({
                        deposit: true,
                        withdrawal: true,
                        book: true,
                      })
                    }
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className="game-btn game-btn--sm"
                    onClick={() =>
                      setHistoryTxFilters({
                        deposit: false,
                        withdrawal: false,
                        book: false,
                      })
                    }
                  >
                    None
                  </button>
                </div>
              </div>
            )}

            <div className="game-modal__body game-modal__body--scroll">
              {(() => {
                const filteredResults = getFilteredHistoryResults();

                if (filteredResults.length === 0) {
                  return historyResults.length > 0 ? (
                    <p className="game-empty">
                      No matching transactions (try adjusting filters).
                    </p>
                  ) : (
                    !isHistoryLoading && (
                      <p className="game-empty">No transactions found.</p>
                    )
                  );
                }

                let lastSessionId = null;
                let currentColorIndex = 1;

                return (
                  <div style={{ overflowX: "auto" }}>
                    {historySearchType === "player" &&
                      (filteredResults.some((t) => t.player_book !== 0) ||
                        filteredResults.some((t) => t.player_credit !== 0)) && (
                      <div style={{ overflowX: "auto", display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap", marginBottom: "10px" }}>
                        {filteredResults.some((t) => t.player_credit != 0 && t.player_credit != null) && (
                          <span className="game-player-badge game-player-badge--session-yellow">
                            Session Credit
                          </span>
                        )}
                        {filteredResults.some((t) => t.player_book > 0) && (
                          <span className="game-player-badge game-player-badge--debt">
                            Account Debt
                          </span>
                        )}
                        {filteredResults.some((t) => t.player_book < 0) && (
                          <span className="game-player-badge game-player-badge--credit">
                            Player Balance
                          </span>
                        )}
                      </div>
                    )}
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
                      <thead>
                        <tr style={{ borderBottom: "2px solid var(--game-border)", textAlign: "left" }}>
                          <th style={{ padding: "0.75rem" }}>Time</th>
                          <th style={{ padding: "0.75rem" }}>Action</th>
                          <th style={{ padding: "0.75rem" }}>Amount</th>
                          <th style={{ padding: "0.75rem" }}>Method</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredResults.map((tx, idx) => {
                          const isNewSession = tx.session_id !== lastSessionId;
                          if (isNewSession) {
                            currentColorIndex = (currentColorIndex + 1) % 2;
                            lastSessionId = tx.session_id;
                          }

                          const bg =
                            currentColorIndex === 1
                              ? "rgba(49, 130, 206, 0.15)"
                              : "rgba(236, 201, 75, 0.1)";

                          return (
                            <tr
                              key={`${tx.source || historySearchType}-${tx.id || idx}`}
                              style={{ borderBottom: "1px solid var(--game-border)", background: bg }}
                            >
                              <td style={{ padding: "0.75rem", whiteSpace: "nowrap" }}>
                                <div style={{ fontWeight: 500 }}>
                                  {formatTime(tx.completed_at)}, {formatDate(tx.completed_at)}
                                </div>
                              </td>
                              <td style={{ padding: "0.75rem" }}>
                                {formatTransactionCode(tx.code)}
                              </td>
                              <td style={{ padding: "0.75rem" }}>
                                <div className="game-player-badges-container" style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "4px", width: "100%" }}>
                                  <span style={{ fontWeight: 700 }}>
                                    {formatCurrency(tx.amount)}
                                  </span>

                                  <div style={{ display: "flex", gap: "4px", marginLeft: "auto" }}>
                                    {tx.player_credit != 0 && tx.player_credit != null && (
                                      <span className="game-player-badge game-player-badge--session-yellow">
                                        ${tx.player_credit}
                                      </span>
                                    )}
                                    {tx.player_book > 0 && (
                                      <span className="game-player-badge game-player-badge--debt">
                                        ${tx.player_book}
                                      </span>
                                    )}
                                    {tx.player_book < 0 && (
                                      <span className="game-player-badge game-player-badge--credit">
                                        ${Math.abs(tx.player_book)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td style={{ padding: "0.75rem" }}>
                                {tx.method || ""}
                                {tx.receiver_name && (
                                  <span style={{ color: "var(--game-text-muted)" }}>
                                    {(WITHDRAWAL_CODES.includes(tx.code) ? " from " : " to ") + tx.receiver_name}
                                  </span>
                                )}
                                {historySearchType === "wallet" && tx.entity_name && (
                                  <span style={{ color: "var(--game-text-muted)" }}>
                                    {" "}{tx.entity_name}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {hasMoreHistory && (
                      <div style={{ textAlign: "center", marginTop: "1.5rem", paddingBottom: "1rem" }}>
                        <button
                          className="game-btn game-btn--secondary"
                          onClick={loadMoreDashboardHistory}
                          disabled={isHistoryLoading}
                        >
                          {isHistoryLoading ? "Loading..." : "Load More"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* Session Payouts Modal */}
      {selectedSessionId !== null && (
        <>
          <div className="game-modal-overlay" onClick={() => setSelectedSessionId(null)} />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Session Payouts - {formatDate(sessions.find((s) => s.id === selectedSessionId)?.start_time)}
              </h3>
              <button className="game-icon-btn" onClick={() => setSelectedSessionId(null)}><FaTimes /></button>
            </div>
            
            <div className="game-modal__filters" style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                {[
                  { key: "staff", label: "Staff" },
                  { key: "costs", label: "Costs" },
                  { key: "wallets", label: "Wallets" },
                ].map(({ key, label }) => (
                  <label key={key} className="game-filter-chip">
                    <input type="checkbox" checked={sessionPayoutFilters[key]} onChange={(e) => setSessionPayoutFilters(p => ({ ...p, [key]: e.target.checked }))} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <div style={{ display: "flex", gap: "0.5rem", marginLeft: "auto" }}>
                <button className="game-btn game-btn--sm game-btn--secondary" onClick={() => setSessionPayoutFilters({ staff: true, costs: true, wallets: true })}>All</button>
                <button className="game-btn game-btn--sm game-btn--secondary" onClick={() => setSessionPayoutFilters({ staff: false, costs: false, wallets: false })}>None</button>
              </div>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {!sessionTransactions[selectedSessionId] ? <p className="game-empty">Loading...</p> : (
                <div className="game-transaction-list">
                  {sessionTransactions[selectedSessionId].filter((t) => {
                    const code = Number(t.code);
                    if ([24, 26, 27, 28, 29].includes(code)) return sessionPayoutFilters.staff;
                    if (code === 32) return sessionPayoutFilters.costs;
                    if (code === 36 || t.wallet_id) return sessionPayoutFilters.wallets;
                    return true;
                  }).map((txn) => {
                    const amt = parseFloat(txn.amount || 0);
                    const code = Number(txn.code);
                    const title = formatTransactionCode(txn.code, txn.type, txn.method);

                    return (
                      <div key={txn.id} className="game-transaction-item" style={{ 
                        padding: "1rem 1.25rem"
                      }}>
                        <span className="game-transaction-time" style={{ width: "80px" }}>{formatTime(txn.completed_at || txn.created_at)}</span>
                        <span className="game-transaction-amount" style={{ width: "70px", fontSize: "1.1rem", fontWeight: 800, color: "var(--game-text-primary)" }}>${Math.abs(amt).toFixed(0)}</span>
                        
                        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "0.75rem" }}>
                           <strong style={{ fontSize: "1rem" }}>{title}</strong>
                           {txn.name && (
                             <span style={{ color: "var(--game-text-muted)" }}>
                               {([36].includes(code) || txn.wallet_id) ? (amt > 0 ? " to " : " from ") : ""}
                               {txn.name}
                             </span>
                           )}
                           {txn.description && <span style={{ fontStyle: "italic", color: "var(--game-text-muted)", fontSize: "0.85rem" }}>{txn.description}</span>}
                        </div>

                        <span className="game-transaction-date" style={{ width: "100px", textAlign: "right", color: "var(--game-text-muted)" }}>{formatDate(txn.completed_at || txn.created_at)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {selectedSessionCashId !== null && (
        <>
          <div className="game-modal-overlay" onClick={() => setSelectedSessionCashId(null)} />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Cash Transactions</h3>
              <button className="game-icon-btn" onClick={() => setSelectedSessionCashId(null)}><FaTimes /></button>
            </div>
            <div className="game-modal__body game-modal__body--scroll">
              <div className="game-transaction-list">
                {sessionCashTransactions[selectedSessionCashId]?.map((t) => {
                  const isVerify = t.type === "verify";
                  const isRefill = t.type === "refill";
                  const isClear = t.type === "clear";
                  const amt = parseFloat(t.amount);
                  return (
                    <div key={t.id} className="game-transaction-item">
                      <span className="game-transaction-time">{formatTime(t.completed_at)}</span>
                      <strong className="game-transaction-player">{t.player_name || ""}</strong>
                      <span className={`game-transaction-amount ${amt < 0 ? "game-text-danger" : "game-text-success"}`}>${Math.abs(amt).toFixed(0)}</span>
                      {isVerify ? <span className="game-badge game-badge--credit">verification</span> : isRefill ? <span className="game-badge game-badge--running">refill</span> : isClear ? <span className="game-badge game-badge--debt">cleared</span> : <><span className="game-transaction-type">{formatTransactionCode(t.code, t.type, t.method)}</span>{t.table_name && <span className="game-transaction-table">{t.table_name}</span>}</>}
                      <span className="game-transaction-date">{formatDate(t.completed_at)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {showBonusModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => {
              setShowBonusModal(false);
              setBonusSessionId(null);
            }}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Bonuses - {formatDate(sessions.find((s) => s.id === bonusSessionId)?.start_time)}
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => {
                  setShowBonusModal(false);
                  setBonusSessionId(null);
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {bonusTransactions.length === 0 ? <p className="game-empty">No bonuses.</p> : (
                <div className="game-transaction-list">
                  {bonusTransactions.map((bonus) => (
                    <div key={bonus.id} className="game-transaction-item">
                      <span className="game-transaction-time">{formatTime(bonus.completed_at)}</span>
                      <strong className="game-transaction-player">{bonus.player_name}</strong>
                      <span className="game-transaction-amount">${parseFloat(bonus.amount).toFixed(0)}</span>
                      <span className="game-transaction-type">{bonus.book === 1 ? "Book" : "Deposit"}</span>
                      {bonus.table_name && <span className="game-transaction-table">{bonus.table_name}</span>}
                      <span className="game-transaction-date">{formatDate(bonus.completed_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
          </>
        )}
      </div>
    </div>
  );
}

const formatCurrency = (amount) => {
  const num = parseFloat(amount);
  return num >= 0 ? `$${num.toFixed(0)}` : `-$${Math.abs(num).toFixed(0)}`;
};

const formatCompactCurrency = (amount) => {
  const num = Number(amount) || 0;
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  if (abs >= 1000000) {
    return `${sign}$${(abs / 1000000).toFixed(abs >= 10000000 ? 0 : 1)}M`;
  }

  if (abs >= 1000) {
    return `${sign}$${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}K`;
  }

  return `${sign}$${abs.toFixed(0)}`;
};

const formatSignedCompactCurrency = (amount) => {
  const num = Number(amount) || 0;
  if (num === 0) return "$0";

  return `${num > 0 ? "+" : ""}${formatCompactCurrency(num)}`;
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch (e) { return "N/A"; }
};

const formatTime = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch (e) { return "N/A"; }
};

function getLocalDateKey(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getMonthDateRange(monthDate) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();

  return {
    start: getLocalDateKey(new Date(year, month, 1)),
    end: getLocalDateKey(new Date(year, month + 1, 0)),
  };
}

function buildCalendarDays(monthDate, sessionsByDay) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leadingDays = firstDay.getDay();
  const slotCount = Math.ceil((leadingDays + daysInMonth) / 7) * 7;
  const todayKey = getLocalDateKey(new Date());

  return Array.from({ length: slotCount }, (_, index) => {
    const date = new Date(year, month, index - leadingDays + 1);
    const dateKey = getLocalDateKey(date);

    return {
      date,
      dateKey,
      data: sessionsByDay[dateKey],
      inCurrentMonth: date.getMonth() === month,
      isToday: dateKey === todayKey,
    };
  });
}

function PerformanceCalendar({ days }) {
  return (
    <div style={{ overflowX: "auto", paddingBottom: "0.25rem" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(92px, 1fr))", gap: "0.5rem", minWidth: "720px" }}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div
            key={day}
            style={{
              color: "#94a3b8",
              fontSize: "0.7rem",
              fontWeight: "800",
              letterSpacing: "0.04em",
              padding: "0 0.35rem",
              textTransform: "uppercase"
            }}
          >
            {day}
          </div>
        ))}

        {days.map((day) => {
          const data = day.data;
          const hasData = Boolean(data?.sessions);
          const profit = Number(data?.totalProfit) || 0;
          const profitColor = profit >= 0 ? "#10b981" : "#f43f5e";

          return (
            <div
              key={day.dateKey}
              style={{
                minHeight: "132px",
                padding: "0.6rem",
                borderRadius: "0.5rem",
                border: day.isToday ? "1px solid #3182ce" : `1px solid ${hasData ? "#475569" : "#334155"}`,
                background: hasData ? "rgba(15, 23, 42, 0.95)" : "rgba(15, 23, 42, 0.45)",
                opacity: day.inCurrentMonth ? 1 : 0.42,
                display: "flex",
                flexDirection: "column",
                gap: "0.45rem",
                boxShadow: hasData ? `inset 0 3px 0 ${profitColor}` : "none"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
                <span style={{ color: day.isToday ? "#93c5fd" : "#cbd5e1", fontSize: "0.8rem", fontWeight: "800" }}>
                  {day.date.getDate()}
                </span>
                {hasData && (
                  <span style={{ color: profitColor, fontSize: "0.8rem", fontWeight: "800", whiteSpace: "nowrap" }}>
                    {formatCompactCurrency(profit)}
                  </span>
                )}
              </div>

              {hasData && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.35rem" }}>
                    <CalendarMetric label="Rake" value={formatCompactCurrency(data.rake)} />
                    <CalendarMetric label="Costs" value={formatCompactCurrency(data.costs)} />
                    <CalendarMetric label="Tips" value={formatCompactCurrency(data.tips)} />
                    <CalendarMetric
                      label="Book"
                      value={formatSignedCompactCurrency(data.bookChange)}
                      color={(Number(data.bookChange) || 0) > 0 ? "#f43f5e" : (Number(data.bookChange) || 0) < 0 ? "#10b981" : "#e2e8f0"}
                    />
                    <CalendarMetric label="Tables" value={data.tables || 0} />
                    <CalendarMetric label="Players" value={data.players || 0} />
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CalendarMetric({ label, value, color }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ color: "#64748b", fontSize: "0.58rem", fontWeight: "800", lineHeight: 1.1, textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ color: color || "#e2e8f0", fontSize: "0.72rem", fontWeight: "700", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {value}
      </div>
    </div>
  );
}

function SessionDayTick({ x, y, payload, index, totalTicks = 0 }) {
  const [dayName, dateLabel] = String(payload?.value || "").split("|");
  const tickIndex = Number.isInteger(index) ? index : Number(payload?.index || 0);
  const shouldShowLabel = totalTicks <= 10 || tickIndex % 5 === 0;

  if (!shouldShowLabel) {
    return <g transform={`translate(${x},${y})`} />;
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill="#94a3b8" fontSize={10}>
        <tspan x="0" dy="0">{dayName}</tspan>
        {dateLabel && <tspan x="0" dy="14" fill="#64748b">{dateLabel}</tspan>}
      </text>
    </g>
  );
}

const MetricItem = ({ label, value, color, bold }) => (
  <div style={{ display: "flex", flexDirection: "column" }}>
    <div style={{ fontSize: "0.65rem", fontWeight: "700", color: "#64748b", marginBottom: "0.25rem", letterSpacing: "0.05em" }}>{label}</div>
    <div style={{ fontSize: "1rem", fontWeight: bold ? "800" : "600", color: color || "#f8fafc" }}>{value}</div>
  </div>
);

function SessionDetailModal({ session, details, activeTab, setActiveTab, onClose }) {
  if (!session) return null;

  return (
    <div className="game-modal-overlay" style={{ display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div 
        className="game-modal game-modal--large" 
        style={{ width: "90%", maxWidth: "1000px", maxHeight: "90vh", background: "#1e293b", border: "1px solid #334155", display: "flex", flexDirection: "column", overflow: "hidden" }}
        onClick={e => e.stopPropagation()}
      >
        <div className="game-modal__header" style={{ borderBottom: "1px solid #334155", padding: "1.5rem", flexShrink: 0 }}>
          <div>
            <h3 className="game-modal__title" style={{ fontSize: "1.25rem", color: "#f8fafc" }}>
              Session Detail - {new Date(session.start_time).toLocaleDateString()}
            </h3>
            <p style={{ fontSize: "0.875rem", color: "#94a3b8", margin: "0.25rem 0 0" }}>
              {new Date(session.start_time).toLocaleTimeString()} - {session.end_time ? new Date(session.end_time).toLocaleTimeString() : "Ongoing"}
            </p>
          </div>
          <button className="game-btn game-btn--ghost" onClick={onClose} style={{ color: "#94a3b8" }}>
            <X size={24} />
          </button>
        </div>

        <div className="game-modal__tabs" style={{ display: "flex", gap: "0.75rem", padding: "0.75rem 1.5rem", background: "#0f172a", flexShrink: 0, overflowX: "auto" }}>
          {[
            { id: 'tables', label: 'Tables', icon: <Table size={14} /> },
            { id: 'tournaments', label: 'Tours', icon: <Trophy size={14} /> },
            { id: 'transactions', label: 'Payouts', icon: <Activity size={14} /> },
            { id: 'cash', label: 'Cash', icon: <DollarSign size={14} /> },
            { id: 'book', label: 'Book', icon: <BookOpen size={14} /> },
            { id: 'wallets', label: 'Wallets', icon: <CreditCard size={14} /> },
            { id: 'bonuses', label: 'Bonuses', icon: <Gift size={14} /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                padding: "0.4rem 0.8rem",
                borderRadius: "0.5rem",
                border: "none",
                cursor: "pointer",
                fontSize: "0.75rem",
                fontWeight: "700",
                transition: "all 0.2s",
                whiteSpace: "nowrap",
                backgroundColor: activeTab === tab.id ? "#6366f1" : "transparent",
                color: activeTab === tab.id ? "white" : "#94a3b8"
              }}
            >
              {tab.icon} {tab.label.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="game-modal__body game-modal__body--scroll" style={{ padding: "1.5rem", color: "#e2e8f0", overflowY: "auto", flexGrow: 1 }}>
          {details.loading ? (
            <div style={{ textAlign: "center", padding: "3rem", color: "#94a3b8" }}>Loading session data...</div>
          ) : (
            <>
              {activeTab === 'tables' && (
                <div className="game-table-list">
                  {details.tables.length === 0 ? <p className="game-empty">No tables in this session.</p> : (
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ textAlign: "left", color: "#94a3b8", borderBottom: "1px solid #334155" }}>
                          <th style={{ padding: "0.75rem" }}>Name/Game</th>
                          <th style={{ padding: "0.75rem" }}>Duration</th>
                          <th style={{ padding: "0.75rem" }}>Rake/Tips</th>
                          <th style={{ padding: "0.75rem" }}>Deposits</th>
                          <th style={{ padding: "0.75rem" }}>Withdrawals</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.tables.map(t => (
                          <tr key={t.id} style={{ borderBottom: "1px solid #334155" }}>
                            <td style={{ padding: "0.75rem" }}>
                              <div style={{ fontWeight: "600", color: "#f8fafc" }}>{t.stakes} {t.game_type}</div>
                              <div style={{ fontSize: "0.75rem", color: "#64748b" }}>{t.name}</div>
                            </td>
                            <td style={{ padding: "0.75rem" }}>
                               <div style={{ fontSize: "0.875rem" }}>{t.length || "00:00:00"}</div>
                               <div style={{ fontSize: "0.7rem", color: t.running ? "#10b981" : "#64748b" }}>{t.running ? "ACTIVE" : "CLOSED"}</div>
                            </td>
                            <td style={{ padding: "0.75rem" }}>
                              <div style={{ color: "#10b981", fontWeight: "600" }}>R: ${t.rake?.toLocaleString()}</div>
                              <div style={{ color: "#94a3b8", fontSize: "0.75rem" }}>T: ${t.tips?.toLocaleString()}</div>
                            </td>
                            <td style={{ padding: "0.75rem" }}>
                              <div style={{ color: "#818cf8" }}>{t.deposits_count} <span style={{ fontSize: "0.75rem" }}>qty</span></div>
                              <div style={{ fontWeight: "700" }}>${t.deposits_total?.toLocaleString()}</div>
                            </td>
                            <td style={{ padding: "0.75rem" }}>
                              <div style={{ color: "#f43f5e" }}>{t.withdrawals_count} <span style={{ fontSize: "0.75rem" }}>qty</span></div>
                              <div style={{ fontWeight: "700" }}>${t.withdrawals_total?.toLocaleString()}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {activeTab === 'tournaments' && (
                <div className="game-tournament-list">
                  {details.tournaments.length === 0 ? <p className="game-empty">No tournaments in this session.</p> : (
                    <div style={{ display: "grid", gap: "1rem" }}>
                      {details.tournaments.map(t => (
                        <div key={t.id} style={{ padding: "1rem", background: "#0f172a", borderRadius: "0.75rem", border: "1px solid #334155" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                            <span style={{ fontWeight: "600", fontSize: "1rem" }}>{t.name}</span>
                            <span style={{ color: t.profit >= 0 ? "#10b981" : "#f43f5e", fontWeight: "700" }}>Profit: ${t.profit?.toLocaleString()}</span>
                          </div>
                          <div style={{ fontSize: "0.8rem", color: "#94a3b8", display: "flex", gap: "1rem" }}>
                            <span>Entries: {t.total_entries || 0}</span>
                            <span>Rake: ${t.rake || 0}</span>
                            <span>Tips: ${t.tips || 0}</span>
                            <span>Rebuys: {t.rebuys_num || 0}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'transactions' && (
                <div className="game-transaction-list">
                  {details.transactions.length === 0 ? <p className="game-empty">No payouts or costs recorded.</p> : (
                    <div style={{ display: "grid", gap: "0.75rem" }}>
                      {details.transactions.map((txn, idx) => (
                        <div key={idx} style={{ 
                          padding: "0.75rem 1rem", 
                          background: "#0f172a", 
                          borderRadius: "0.5rem", 
                          display: "flex", 
                          justifyContent: "space-between", 
                          alignItems: "center",
                          borderLeft: `3px solid ${txn.type === 'cost' ? '#f43f5e' : '#818cf8'}`
                        }}>
                          <div>
                            <div style={{ fontWeight: "600" }}>{(txn.name || txn.type || "").replace(/_/g, ' ').toUpperCase()}</div>
                            <div style={{ fontSize: "0.75rem", color: "#64748b" }}>{txn.method} {txn.description ? `• ${txn.description}` : ''}</div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontWeight: "700", color: txn.amount > 0 ? "#10b981" : "#f43f5e" }}>
                              ${Math.abs(txn.amount).toLocaleString()}
                            </div>
                            <div style={{ fontSize: "0.7rem", color: "#64748b" }}>{new Date(txn.completed_at || txn.timestamp).toLocaleTimeString()}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            {activeTab === 'book' && (
              <div style={{ padding: "0.5rem" }}>
                {!details.bookChanges ? (
                  <div style={{ color: "#64748b" }}>Loading book audit...</div>
                ) : details.bookChanges.length === 0 ? (
                  <div style={{ background: "#0f172a", padding: "2rem", borderRadius: "0.5rem", textAlign: "center", color: "#64748b", border: "1px dashed #334155" }}>
                    No book changes recorded in this session.
                  </div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", color: "#f8fafc" }}>
                    <thead>
                      <tr style={{ background: "#0f172a", textAlign: "left" }}>
                        <th style={{ padding: "0.75rem", borderBottom: "1px solid #334155" }}>Player</th>
                        <th style={{ padding: "0.75rem", borderBottom: "1px solid #334155" }}>Old Book</th>
                        <th style={{ padding: "0.75rem", borderBottom: "1px solid #334155" }}>Net Change</th>
                        <th style={{ padding: "0.75rem", borderBottom: "1px solid #334155" }}>Current Book</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.bookChanges.map((b, idx) => (
                        <tr key={idx} style={{ borderBottom: "1px solid #1e293b", background: idx % 2 === 0 ? "transparent" : "#0f172a80" }}>
                          <td style={{ padding: "0.75rem", fontWeight: "600" }}>{b.player_name}</td>
                          <td style={{ padding: "0.75rem" }}>${b.old_book?.toLocaleString()}</td>
                          <td style={{ padding: "0.75rem", color: b.book_change >= 0 ? "#10b981" : "#f43f5e", fontWeight: "700" }}>
                            {b.book_change > 0 ? "+" : ""}${b.book_change?.toLocaleString()}
                          </td>
                          <td style={{ padding: "0.75rem" }}>${b.current_book?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === 'wallets' && (
              <div style={{ padding: "0.5rem" }}>
                {!details.walletChanges ? (
                  <div style={{ color: "#64748b" }}>Loading wallet audit...</div>
                ) : details.walletChanges.length === 0 ? (
                  <div style={{ background: "#0f172a", padding: "2rem", borderRadius: "0.5rem", textAlign: "center", color: "#64748b", border: "1px dashed #334155" }}>
                    No wallet balance changes this session.
                  </div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", color: "#f8fafc" }}>
                    <thead>
                      <tr style={{ background: "#0f172a", textAlign: "left" }}>
                        <th style={{ padding: "0.75rem", borderBottom: "1px solid #334155" }}>Wallet</th>
                        <th style={{ padding: "0.75rem", borderBottom: "1px solid #334155" }}>Opening Balance</th>
                        <th style={{ padding: "0.75rem", borderBottom: "1px solid #334155" }}>Net Movement</th>
                        <th style={{ padding: "0.75rem", borderBottom: "1px solid #334155" }}>Closing Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.walletChanges.map((w, idx) => (
                        <tr key={idx} style={{ borderBottom: "1px solid #1e293b", background: idx % 2 === 0 ? "transparent" : "#0f172a80" }}>
                          <td style={{ padding: "0.75rem", fontWeight: "600" }}>{w.wallet_name}</td>
                          <td style={{ padding: "0.75rem" }}>${w.old_balance?.toLocaleString()}</td>
                          <td style={{ padding: "0.75rem", color: w.balance_change >= 0 ? "#10b981" : "#f43f5e", fontWeight: "700" }}>
                            {w.balance_change > 0 ? "+" : ""}${w.balance_change?.toLocaleString()}
                          </td>
                          <td style={{ padding: "0.75rem" }}>${w.current_balance?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === 'bonuses' && (
              <div className="game-modal__body game-modal__body--scroll">
                {details.bonuses?.length === 0 ? <p className="game-empty">No bonuses recorded.</p> : (
                  <div className="game-transaction-list">
                    {(details.bonuses || []).map((bonus) => (
                      <div key={bonus.id} className="game-transaction-item">
                        <span className="game-transaction-time">{formatTime(bonus.completed_at)}</span>
                        <strong className="game-transaction-player">{bonus.player_name}</strong>
                        <span className="game-transaction-amount">${parseFloat(bonus.amount).toFixed(0)}</span>
                        <span className="game-transaction-type">{bonus.book === 1 ? "Book" : "Deposit"}</span>
                        {bonus.table_name && <span className="game-transaction-table">{bonus.table_name}</span>}
                        <span className="game-transaction-date">{formatDate(bonus.completed_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, subValue, icon, trend, description }) {
  return (
    <div style={{ background: "#1e293b", padding: "1.25rem", borderRadius: "1rem", border: "1px solid #334155", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
        <span style={{ color: "#94a3b8", fontSize: "0.875rem", fontWeight: "600" }}>{title}</span>
        <div style={{ background: "#0f172a", padding: "0.5rem", borderRadius: "0.5rem" }}>{icon}</div>
      </div>
      <div>
        <div style={{ fontSize: "1.5rem", fontWeight: "800", color: "#f8fafc" }}>{value}</div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.25rem", marginTop: "0.25rem" }}>
          {trend && (
            trend === "up" ? <TrendingUp size={14} color="#10b981" /> : <TrendingDown size={14} color="#f43f5e" />
          )}
          <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>{subValue}</span>
        </div>
        {description && <div style={{ fontSize: "0.65rem", color: "#64748b", marginTop: "0.5rem" }}>{description}</div>}
      </div>
    </div>
  );
}
